/**
 * PreRunScreenStyles — token-driven, lit-CRT panel material.
 * All colours resolve from palette.ts / COLORS / uiTokens — NO new gameplay hex.
 * Replaces the old bespoke chrome (Courier, #0f2f0f, ⌐ brackets).
 */
import { COLORS } from "../core/constants.ts";
import { ECHELON } from "../rendering/designTokens.ts";
import {
  UI_CHROME,
  UI_FONT_STACK,
  UI_SPACE,
  roleFontSize,
  withUiScale,
} from "./uiTokens.ts";

/**
 * AC-27 (2026-prestart-deployment-redesign) touch floor for this screen's
 * `@media (pointer:coarse)` blocks — `max(44px, <64 counter-scaled>)`,
 * the SAME `max(44px, withUiScale(N))` idiom `modalShell.ts`'s
 * `MODAL_TOUCH_FLOOR` and `AudioMixer.ts` already ship (never a bare
 * design-px literal inside the stage — `knowledge/system/ios-platform.md`).
 *
 * The base argument is 64, not 44: this spec's AC-27 is a HARD ≥44 REAL px
 * requirement (measured by `tests/e2e/prerun-rails.spec.ts`), unlike
 * `MODAL_TOUCH_FLOOR`'s documented "best reachable" 44 (its own doc-block
 * explains why 44 undershoots on the phone tier). At the tighter of the two
 * gated profiles — 667x375, where `k = --ui-scale * --chrome-scale *
 * stageScale = 1.25 * 1.391318 * 0.416615 = 0.72454` — 64 clears 44 real px
 * with a ~2.4px margin (64 * 0.72454 = 46.37); the roomier 852x393 profile
 * (`k = 1.25 * 1.352564 * 0.436667 = 0.73828`) clears with ~3.2px
 * (64 * 0.73828 = 47.25). Both `k`s re-derived from `stageLayout.ts`'s
 * `computeChromeScale`/`computeStageFit`, cross-checked against a real
 * Chromium measurement (`docs/session_log/prerun-rails-2026-08-15.md`).
 */
const PRERUN_TOUCH_FLOOR = `max(44px, ${withUiScale(64)})`;

export function getPreRunStyles(): string {
  const G = COLORS.SENTINEL_GREEN;
  const GH = UI_CHROME.greenHi;
  const GD = COLORS.SENTINEL_GREEN_DIM;
  const CRIM = COLORS.ORACLE_RED;
  const GOLD = UI_CHROME.etchAccent;
  const PB = UI_CHROME.panelBorder;
  const GT = UI_CHROME.groundTop;
  const GB = UI_CHROME.groundBottom;
  const DIM = UI_CHROME.dim;
  const STEEL = UI_CHROME.steel;
  const GDK = UI_CHROME.greenDk;
  const GOLDK = UI_CHROME.goldDk;
  return `
#pre-run-screen {
  position:absolute; inset:0; display:flex; flex-direction:column;
  z-index:100; font-family:${UI_FONT_STACK}; overflow:hidden;
  background:radial-gradient(120% 80% at 50% -10%,#0c120d 0%,#06090a 60%);
}

/* ── Living arena atmosphere (z below panels, reduced-motion-safe) ─── */
.prerun-atmosphere { position:absolute; inset:0; z-index:0; pointer-events:none; overflow:hidden; }
.prerun-atmo-grid {
  position:absolute; left:-30%; right:-30%; bottom:-12%; height:78%;
  background:
    linear-gradient(rgba(136, 224, 208,.07) 1px,transparent 1px) 0 0/54px 54px,
    linear-gradient(90deg,rgba(136, 224, 208,.07) 1px,transparent 1px) 0 0/54px 54px;
  transform:perspective(600px) rotateX(63deg); transform-origin:bottom center; opacity:.30;
  animation:prerun-griddrift 18s linear infinite;
  -webkit-mask-image:linear-gradient(transparent,#000 72%);
  mask-image:linear-gradient(transparent,#000 72%);
}
@keyframes prerun-bgdrift {
  from { transform:scale(1.06) translate3d(-0.8%,-0.5%,0); }
  to   { transform:scale(1.06) translate3d(0.8%,0.5%,0); }
}
@keyframes prerun-griddrift { 0%{background-position:0 0,0 0} 100%{background-position:0 54px,54px 0} }
.prerun-atmo-haze {
  position:absolute; inset:0;
  background:
    radial-gradient(85% 55% at 50% 26%,rgba(136, 224, 208,.06),transparent 60%),
    radial-gradient(60% 45% at 84% 80%,rgba(232, 154, 184,.05),transparent 60%);
}
.prerun-atmo-vig { position:absolute; inset:0; background:radial-gradient(135% 112% at 50% 44%,transparent 50%,rgba(0,0,0,.62) 100%); }

/* ── COMMAND LINE (Richtung C, §A) ──────────────────────────────────────
   ABORT · the mission line · the ⚙, on ONE touch-high row that FLOATS over the
   hero image. It replaces two bands that used to stack above the stage: the
   42px '.prerun-topbar' (back · an "AI WARS — MAINFRAME DEFENSE" title read by
   someone already inside the game · gear) and the separate '.prerun-mission'
   row. On the 393px-high landscape profile those two cost ~14% of the screen
   for context the mockup gets for free over the picture, which is why the
   title is dropped and the mission line takes the middle.

   It reads over the top of the stage rather than in a band of its own — the
   393px-high landscape profile cannot spare a band for it. */
.prerun-cmd {
  position:absolute; left:0; right:0; top:0; z-index:5; box-sizing:border-box;
  height:${PRERUN_TOUCH_FLOOR}; display:flex; align-items:center; gap:${
    UI_SPACE.md
  }px;
  padding:0 14px; pointer-events:none;
}
.prerun-cmd > * { pointer-events:auto; }
/* BL-217 — iPhone only: nudge ABORT (top-left) + the ⚙ (top-right) inward off
   the rounded display corners so they are never clipped. PHONE-TIER gated
   (iPad keeps the tight 14px inset — gentle corners, no Dynamic Island).
   Calibrated against a device photo; it moves here with the controls it
   protects, because the mockup has no rounded corners and the device does. */
html.phone-tier .prerun-cmd {
  /* Device round 2026-08-18: the leading inset also yields to the Dynamic
     Island (landscape: LEFT edge) via the real per-side safe-area. */
  padding-left:max(44px, calc(var(--inset-left-design, 0px) + 6px));
  padding-right:44px;
}
.prerun-cmd-back {
  flex:0 0 auto; margin-right:calc(${UI_SPACE.xxl}px * 2);
  display:inline-flex; align-items:center; justify-content:center;
  min-height:${PRERUN_TOUCH_FLOOR}; gap:5px; color:${G};
  letter-spacing:.18em; font-size:${roleFontSize(
    "body"
  )}; cursor:pointer; font-family:${UI_FONT_STACK};
  border:1px solid rgba(136, 224, 208,.32); border-radius:6px; padding:0 18px;
  background:linear-gradient(180deg,rgba(10,18,16,.78),rgba(5,8,11,.72));
  box-shadow:0 0 18px -8px rgba(136, 224, 208,.5);
  animation:prerun-backpulse 3.6s ease-in-out infinite;
  -webkit-tap-highlight-color:transparent;
}
@keyframes prerun-backpulse {
  0%,100%{box-shadow:0 0 0 1px rgba(136, 224, 208,.06);border-color:rgba(136, 224, 208,.28)}
  50%{box-shadow:0 0 15px -5px rgba(136, 224, 208,.55);border-color:rgba(136, 224, 208,.6)}
}
.prerun-cmd-back:hover { animation:none; border-color:${G}; box-shadow:0 0 16px -4px rgba(136, 224, 208,.6); }
/* DEVICE ROUND 2026-08-18 (owner): BACK lives in the console CTA row, beside
   WEITER/DEPLOY — same visual language as the old header control, margins
   reset for the row context. */
.prerun-cta-row { display:flex; align-items:stretch; gap:${UI_SPACE.md}px; min-width:0; }
.prerun-console-back { margin-right:0; flex:0 0 auto; }
/* The mission line: a slow beating carrier dot, then protocol + objective on
   ONE non-wrapping row that ellipsises rather than pushing the ⚙ off-screen. */
.prerun-cmd-mission {
  flex:1 1 auto; min-width:0; display:flex; align-items:center;
  justify-content:flex-start; gap:${
    UI_SPACE.md
  }px; overflow:hidden; white-space:nowrap;
}
.prerun-cmd-dot {
  align-self:center; flex:0 0 auto; width:9px; height:9px; border-radius:50%;
  background:${G}; box-shadow:0 0 14px ${G}; animation:prerun-cmdbeat 2400ms ease-in-out infinite;
}
@keyframes prerun-cmdbeat { 0%,100%{opacity:1} 50%{opacity:.38} }
.prerun-topbar-audio { display:flex; align-items:center; gap:8px; min-width:0; }
.prerun-mute-btn {
  background:transparent; border:1px solid ${GD}; color:${G}; font-family:${UI_FONT_STACK};
  font-size:${roleFontSize(
    "body"
  )}; letter-spacing:.12em; padding:3px 9px; border-radius:5px; cursor:pointer; transition:all .2s;
}
.prerun-mute-btn:hover { border-color:${G}; box-shadow:0 0 8px ${G}; }
.prerun-mute-btn.muted { color:${GD}; text-decoration:line-through; opacity:0.8; }
/* BL-115 — the EN/DE locale toggle (shares the mute-button chrome vocabulary) */
.prerun-lang-btn {
  background:transparent; border:1px solid ${GD}; color:${G}; font-family:${UI_FONT_STACK};
  font-size:${roleFontSize(
    "body"
  )}; letter-spacing:.12em; padding:3px 9px; border-radius:5px; cursor:pointer; transition:all .2s;
}
.prerun-lang-btn:hover { border-color:${G}; box-shadow:0 0 8px ${G}; }

/* RESET PROGRESS — unobtrusive danger control (click-to-arm confirm) */
.prerun-reset-btn {
  background:${GB}; border:1px solid ${CRIM}; color:${CRIM}; font-family:${UI_FONT_STACK};
  font-size:${roleFontSize(
    "body"
  )}; letter-spacing:.13em; padding:3px 9px; border-radius:5px; cursor:pointer;
  white-space:nowrap; opacity:.6; transition:all .16s;
}
/* AC-23 (2026-prestart-deployment-redesign) — decorative-only hover: no
   opacity/visibility/display/max-height/content change (that list is what
   AC-23 forbids screen-wide, not just for the privacy affordance below). */
.prerun-reset-btn:hover { filter:brightness(1.3); box-shadow:0 0 12px -4px ${CRIM}; }
.prerun-reset-btn.armed {
  background:${CRIM}; color:#10040a; opacity:1;
  animation:prerun-resetarm .8s ease-in-out infinite;
}
@keyframes prerun-resetarm {
  0%,100%{box-shadow:0 0 0 1px ${CRIM}} 50%{box-shadow:0 0 16px -3px ${CRIM}}
}

/* BL-130 — the loose top-bar controls, folded into the shared ⚙ gear dropdown
   (one gear on Boot/PreRun/HUD, canonical order). Each control becomes a
   full-width dropdown line. */
/* DEVICE ROUND 2026-08-18 (owner: "sehr sehr klein") — the ⚙ takes a full
   COUNTER-SCALED touch footprint on EVERY pointer type (a bare design-px
   width lands at less than half its size after the stage transform — the
   exact trap PRERUN_TOUCH_FLOOR exists for). The shared gear sheet's 36px
   min-width + coarse-only 44px floor stays for Boot/HUD; this raises
   PreRun's instance only, and the glyph grows with the box. */
#pre-run-screen .prerun-gear-btn {
  min-width:calc(${PRERUN_TOUCH_FLOOR} * 1.05);
  min-height:${PRERUN_TOUCH_FLOOR};
  font-size:calc(${PRERUN_TOUCH_FLOOR} * 0.5);
}

/* ── Main layout ────────────────────────────────────────────────────── */
.prerun-main {
  flex:1; position:relative; z-index:1; min-height:0; min-width:0;
  display:flex; flex-direction:column; gap:${UI_SPACE.lg}px;
  /* No vertical scroll on this stage: both acts are FIXED four-zone layouts
     (D-AC-1 / D-AC-2) that must fit the profile, and a scroll container would
     let a too-tall band hide a broken layout instead of showing it. */
  overflow:hidden;
  width:100%; max-width:1400px; margin:0 auto;
  padding:${UI_SPACE.xl}px;
}
.prerun-main > * { flex-shrink:0; }

/* ── Shared lit-CRT panel material (= uiTokens.UI_PANEL_MATERIAL) ──── */
.prerun-panel {
  position:relative; border:1px solid ${PB}; border-radius:7px;
  background:linear-gradient(180deg,${GT},${GB});
  box-shadow:0 0 14px -4px rgba(136, 224, 208,.12) inset, 0 0 0 1px rgba(136, 224, 208,.04);
  overflow:hidden;
}
.prerun-panel.crimson {
  box-shadow:0 0 16px -4px rgba(232, 154, 184,.16) inset,0 0 0 1px rgba(232, 154, 184,.06);
  border-color:rgba(232, 154, 184,.22);
}
.prerun-panel__header {
  display:flex; align-items:center; gap:7px; padding:6px 11px;
  font-size:${roleFontSize("body")}; letter-spacing:.18em; color:${DIM};
  border-bottom:1px solid ${PB}; background:linear-gradient(180deg,rgba(136, 224, 208,.05),transparent);
}
.prerun-panel__header.gold { color:${GOLD}; }
.prerun-panel__header.gold .prerun-panel__dot { background:${GOLD}; box-shadow:0 0 6px ${GOLD}; }
.prerun-panel__dot { width:6px; height:6px; border-radius:50%; background:${G}; box-shadow:0 0 6px ${G}; flex-shrink:0; }
.prerun-panel__livetag {
  margin-left:auto; color:${CRIM}; font-size:${roleFontSize(
    "body"
  )}; letter-spacing:.14em;
  display:flex; align-items:center; gap:4px;
}
.prerun-panel__livetag i {
  width:5px; height:5px; border-radius:50%; background:${CRIM};
  box-shadow:0 0 6px ${CRIM}; animation:prerun-rec 1.1s steps(2,end) infinite;
}
@keyframes prerun-rec { 0%,50%{opacity:1} 51%,100%{opacity:.15} }
.prerun-panel__body { padding:11px 13px; position:relative; }

/* Board chrome: shared scan + corner etch (= BoardChrome.ts vocabulary) */
.prerun-scan {
  position:absolute; inset:0; pointer-events:none; z-index:6; mix-blend-mode:overlay; opacity:.35;
  background:repeating-linear-gradient(0deg,rgba(255,255,255,.05) 0 1px,transparent 1px 3px);
}
.prerun-etch {
  position:absolute; width:20px; height:20px; z-index:7; opacity:.7; pointer-events:none;
}
.prerun-etch::before,.prerun-etch::after { content:""; position:absolute; background:var(--etch-ac,${GOLD}); }
.prerun-etch.t { top:5px; } .prerun-etch.l { left:5px; } .prerun-etch.r { right:5px; }
.prerun-etch.t::before { top:0; width:12px; height:1.5px; }
.prerun-etch.t::after { top:0; width:1.5px; height:8px; }
.prerun-etch.l::before,.prerun-etch.l::after { left:0; }
.prerun-etch.r::before,.prerun-etch.r::after { right:0; }

/* ── Info band: Briefing (left) + Personnel VS (right) ───────────────── */
.prerun-info-band {
  display:grid; grid-template-columns:1fr 1.38fr; gap:12px;
}

/* Briefing panel */
.prerun-brief-ln { color:${DIM}; font-size:${roleFontSize(
    "body"
  )}; line-height:1.6; margin-bottom:5px; }
.prerun-brief-ln .r { color:${CRIM}; text-shadow:0 0 8px rgba(232, 154, 184,.4); }
.prerun-brief-ln .g { color:${GH}; text-shadow:0 0 8px rgba(136, 224, 208,.35); }
.prerun-brief-ln .hl { color:${STEEL}; }
.prerun-brief-auth { color:${GOLD}; font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "body"
  )}; letter-spacing:.06em; margin:10px 0 6px; }
.prerun-brief-grant { color:${G}; font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "body"
  )}; }
.prerun-brief-grant b { color:${GH}; }

/* Briefing terminal typing */
.prerun-terminal { overflow-y:auto; max-height:240px; padding-right:4px; scrollbar-width:thin; scrollbar-color:${GD} transparent; }
.brief-line { font-size:${roleFontSize(
    "body"
  )}; line-height:1.65; color:${G}; white-space:pre-wrap; animation:brief-fadein 0.1s ease-out; text-shadow:0 0 3px rgba(136, 224, 208,.4); }
@keyframes brief-fadein { from{opacity:0;} to{opacity:1;} }
.brief-warn { color:${
    COLORS.AMBER_WARN
  }; text-shadow:0 0 4px rgba(232, 208, 152,0.4); }
.brief-threat { color:${CRIM}; text-shadow:0 0 4px rgba(232, 154, 184,0.4); }
/* S126: ECHELON's signature reads in HER corruption magenta — distinct from
   the crimson threat lines; seeded here, paid off by the in-run corruption. */
.brief-echelon { color:${
    ECHELON.SIGNAL
  }; text-shadow:0 0 4px rgba(209,32,140,0.45); }
.brief-prompt { color:${G}; font-weight:bold; animation:brief-fadein 0.1s ease-out, brief-blink 1s step-end infinite; }
@keyframes brief-blink { 50%{opacity:0.5;} }

/* Echo zone */
.prerun-echo { border-top:1px solid rgba(136, 224, 208,.18); margin-top:12px; padding-top:10px; }
.prerun-echo-line { opacity:0.85; }

/* ── PERSONNEL VS — VERA ⇄ FENRIR hero ──────────────────────────────── */
.prerun-vs-bg {
  position:absolute; inset:34px 0 0 0; z-index:0; pointer-events:none;
  background:
    radial-gradient(58% 80% at 20% 56%,rgba(136, 224, 208,.12),transparent 56%),
    radial-gradient(58% 80% at 80% 56%,rgba(232, 154, 184,.13),transparent 56%),
    linear-gradient(90deg,rgba(136, 224, 208,.05),transparent 42%,transparent 58%,rgba(232, 154, 184,.06));
}
.prerun-vs-grid {
  position:relative; z-index:1; display:grid;
  grid-template-columns:1fr auto 1fr; align-items:stretch; gap:8px; padding:12px 12px 6px;
}
/* BL-202 — each face is HORIZONTAL: the avatar sits BESIDE its identity body, so
   the avatar gets its full height (shown WHOLE) and the text uses the cell width.
   VERA = avatar | body; FENRIR = body | avatar (mirrored, both flanking the VS). */
.prerun-face {
  position:relative; border-radius:9px; overflow:hidden; padding:8px 9px;
  display:flex; flex-direction:row; align-items:center; gap:10px;
  background:radial-gradient(120% 110% at 50% 32%,#0b1410,#060a07 72%);
  box-shadow:inset 0 0 16px rgba(0,0,0,.85);
}
.prerun-face.player { box-shadow:inset 0 0 16px rgba(0,0,0,.85),0 0 18px -4px rgba(136, 224, 208,.4); border:1px solid rgba(136, 224, 208,.4); }
.prerun-face.opponent { flex-direction:row-reverse; text-align:right; box-shadow:inset 0 0 16px rgba(0,0,0,.85),0 0 18px -4px rgba(232, 154, 184,.4); border:1px solid rgba(232, 154, 184,.4); }
/* The identity body (name · role · gauge / dossier) — flows beside the avatar. */
.prerun-face-body { display:flex; flex-direction:column; gap:4px; min-width:0; flex:1; }
/* Avatar: a fixed-height box; the figure is object-fit:contain so the WHOLE
   character scales into it (NO crop — fixes the clipped-head report). */
.prerun-face .operator-mascot { height:96px; flex-shrink:0; display:flex; align-items:center; justify-content:center; overflow:visible; }
.prerun-face .operator-mascot .mascot-figure { height:100%; display:flex; align-items:center; }
.prerun-face .operator-mascot .mascot-figure img { height:100%; width:auto; max-width:96px; object-fit:contain; }
.prerun-face .face-crt { position:absolute; inset:0; z-index:3; pointer-events:none; background:repeating-linear-gradient(0deg,rgba(0,0,0,.18) 0 1px,transparent 1px 3px); }
.prerun-face .face-role { font-size:${roleFontSize(
    "body"
  )}; letter-spacing:.2em; color:${DIM}; }
.prerun-face .face-name { font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "title"
  )}; letter-spacing:.1em; color:${GH}; text-shadow:0 0 10px rgba(136, 224, 208,.5); line-height:1.05; }
.prerun-face.opponent .face-name { color:${CRIM}; text-shadow:0 0 10px rgba(232, 154, 184,.5); }

/* The mission line's three rules (.prerun-brief-protocol / -obj / -dossier)
   were removed 2026-08-16 with the surface itself: the two-screen delta drops
   the mission context, and no element carries these classes any more. Dead
   selectors ship as bytes and read as "this exists somewhere" to the next
   person, which is worse than absent. */
/* BL-202 — FENRIR THREAT DOSSIER: a tight key→value brief (the antagonist body
   that fills FENRIR's box). All fonts ≥ the 12px micro floor; colours tokenized. */
.prerun-dossier { display:grid; grid-template-columns:auto 1fr; gap:1px 8px; align-items:baseline; margin:1px 0; text-align:left; }
.prerun-dossier dt { font-size:${roleFontSize(
    "body"
  )}; letter-spacing:.12em; color:${DIM}; }
.prerun-dossier dd { margin:0; font-size:${roleFontSize(
    "body"
  )}; letter-spacing:.03em; color:${STEEL}; }
.prerun-dossier dd.threat { color:${CRIM}; text-shadow:0 0 7px rgba(232, 154, 184,.4); }

/* BL-202 — full-width identity row (callsign + privacy) under the VS grid. */
.prerun-idrow {
  position:relative; z-index:1; display:grid; grid-template-columns:auto 1fr; gap:16px; align-items:center;
  margin-top:8px; padding-top:9px; border-top:1px dotted rgba(136, 224, 208,.18);
}

/* 3-cell operator callsign */
.prerun-callsign { display:flex; flex-direction:column; align-items:flex-start; gap:3px; }
.prerun-callsign-lab { display:flex; align-items:center; gap:6px; font-size:${roleFontSize(
    "body"
  )}; letter-spacing:.16em; color:${GDK}; white-space:nowrap; }
.prerun-namefield { display:flex; gap:7px; }
.prerun-namecell {
  width:44px; height:54px; border:1px solid rgba(136, 224, 208,.5); border-radius:7px; outline:none; touch-action:manipulation;
  background:radial-gradient(120% 120% at 50% 38%,rgba(136, 224, 208,.09),transparent);
  box-shadow:0 0 16px -6px rgba(136, 224, 208,.5) inset;
  text-align:center; text-transform:uppercase; font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "display"
  )}; line-height:54px;
  color:${GH}; text-shadow:0 0 12px rgba(136, 224, 208,.55); caret-color:${G}; transition:.12s;
}
.prerun-namecell:focus { border-color:${G}; box-shadow:0 0 18px -4px rgba(136, 224, 208,.7) inset,0 0 10px -2px rgba(136, 224, 208,.5); }
/* Native iOS tap-to-cycle cells (no keyboard): tappable affordance, no caret/selection.
   The ▲▼ chevrons make the "tap top = previous / bottom = next letter" interaction
   discoverable for a first-time player (otherwise a plain cell looks read-only). */
.prerun-namecell--cycle { position:relative; cursor:pointer; caret-color:transparent; user-select:none; -webkit-user-select:none; }
/* ▲▼ affordances drawn as CSS border-triangles (NOT a tiny font — keeps the
   readability-net floor clean) so tap-to-cycle is discoverable. */
.prerun-namecell--cycle::before,
.prerun-namecell--cycle::after {
  content:""; position:absolute; left:50%; transform:translateX(-50%);
  width:0; height:0; border-left:4px solid transparent; border-right:4px solid transparent;
  opacity:.5; pointer-events:none;
}
.prerun-namecell--cycle::before { top:5px; border-bottom:5px solid ${G}; }
.prerun-namecell--cycle::after { bottom:5px; border-top:5px solid ${G}; }
.prerun-namecell--cycle:active { border-color:${G}; box-shadow:0 0 18px -4px rgba(136, 224, 208,.7) inset; }
.prerun-namecell--cycle:active::before, .prerun-namecell--cycle:active::after { opacity:.9; }
.prerun-namefield.invalid .prerun-namecell { border-color:${CRIM}; box-shadow:0 0 16px -6px rgba(232, 154, 184,.5) inset; }

/* BL-96/BL-202 — the submit privacy notice, now on the named caption role
   (13px ≥ the readable floor; was an off-role inline 0.72em) with the full-width
   id-row to breathe. Only the link colour is an inline-free accent class. */
.prerun-privacy-note {
  margin:0; font-family:${UI_FONT_STACK}; font-size:${roleFontSize("body")};
  line-height:1.4; letter-spacing:.02em; color:${DIM};
}
/* AC-25 (2026-prestart-deployment-redesign) — this was font:inherit, and
   NOTHING in its ancestor chain (.prerun-id-privacy to .prerun-id-setter to
   .prerun-idbox) sets an explicit font-size, so it fell all the way back to
   the browser's bare, UNSCALED 16px default — invisible to the --ui-scale/
   --chrome-scale chain entirely. Measured worst offender on the whole
   screen: 6.67 real px. An explicit role fixes it at the source. */
.prerun-privacy-link {
  background:none; border:none; padding:0; font-family:${UI_FONT_STACK};
  font-size:${roleFontSize("body")}; color:${COLORS.ICE_CYAN};
  cursor:pointer; text-decoration:underline; white-space:nowrap;
}
.prerun-privacy-link:hover { text-shadow:0 0 8px rgba(136, 224, 208,.6); }

/* VS clash */
.prerun-clash {
  position:relative; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:4px; z-index:5; padding:0 4px;
}
.prerun-clash::before {
  content:""; position:absolute; top:50%; left:50%; width:58px; height:58px; transform:translate(-50%,-50%); border-radius:50%;
  background:radial-gradient(circle,rgba(255,255,255,.16),transparent 64%);
  box-shadow:0 0 24px rgba(232, 154, 184,.45),0 0 30px rgba(136, 224, 208,.28);
  animation:prerun-clashpulse 2.6s ease-in-out infinite;
}
@keyframes prerun-clashpulse { 0%,100%{opacity:.55;transform:translate(-50%,-50%) scale(.88)} 50%{opacity:1;transform:translate(-50%,-50%) scale(1.16)} }
.prerun-clash-spark { width:1.5px; flex:1; background:linear-gradient(180deg,transparent,${GOLD},transparent); box-shadow:0 0 8px ${GOLD}; animation:prerun-sparkflick 2.6s ease-in-out infinite; }
@keyframes prerun-sparkflick { 0%,100%{opacity:.45} 50%{opacity:.95} }
.prerun-clash-vs { position:relative; font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "display"
  )}; color:#fff; text-shadow:0 0 14px ${CRIM},0 0 22px rgba(136, 224, 208,.5); animation:prerun-vspulse 2.6s ease-in-out infinite; }
@keyframes prerun-vspulse { 0%,100%{transform:scale(1);opacity:.9} 50%{transform:scale(1.1);opacity:1} }

/* Engagement gauges — flow at the end of the identity body (BL-202: no more
   margin-top:auto, which used to drop FENRIR's lone gauge to the bottom of the
   column and leave the black void the owner saw). */
.prerun-gauge { display:flex; flex-direction:column; align-items:flex-start; gap:1px; padding-top:3px; }
.prerun-face.opponent .prerun-gauge { align-items:flex-end; }
.prerun-gauge span { font-size:${roleFontSize(
    "body"
  )}; letter-spacing:.22em; color:${DIM}; }
.prerun-gauge b { font-size:${roleFontSize(
    "body"
  )}; letter-spacing:.16em; font-family:${UI_FONT_STACK}; }
.prerun-gauge i { font-size:${roleFontSize(
    "body"
  )}; letter-spacing:.1em; color:${DIM}; font-style:normal; }
.prerun-gauge.g b { color:${G}; text-shadow:0 0 8px rgba(136, 224, 208,.5); }
.prerun-gauge.o b { color:${CRIM}; text-shadow:0 0 8px rgba(232, 154, 184,.5); animation:prerun-threat 2.2s ease-in-out infinite; }
@keyframes prerun-threat { 0%,100%{opacity:.8} 50%{opacity:1;text-shadow:0 0 13px rgba(232, 154, 184,.85)} }

/* (The DEPLOY console — compact two-equal-box layout + the chamfered, glow-when-
   ready / dark-when-locked DEPLOY button — lives in the S188 recompose block.) */
.prerun-deployhint { font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "body"
  )}; letter-spacing:.06em; color:${
    COLORS.AMBER_WARN
  }; min-height:16px; text-align:left; line-height:1.3; }
/* D-AC-3 — THREE measured failures of the same class before this landed, each
   found by probing every zone's rect rather than guessing: an empty hint
   collapsed the console band; a one-line hint still wrapped to two when the
   operator was locked; and narrowing the action column made it wrap again.
   Every one moved the hero frame on nothing but which option was focused.
   The structural fix is to stop letting the hint's LENGTH decide a band's
   height: it now spans the row's free middle width, where it is one line at
   both profiles and in both locales, and it holds that line box even when it
   has nothing to say. Truncating instead would have bought the geometry with
   D-AC-14(d), which wants the reason NAMED — and clipping is AC-26/B28, an
   open owner decision this pass must not quietly settle. */
.prerun-advance-hint {
  flex:1 1 auto; min-width:0; align-self:center;
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
  min-height:1.3em; text-align:center;
}
.prerun-deployhint.blank { color:transparent; }

/* ══════════════════════════════════════════════════════════════════════════
   HERO STAGE + ROTARY — 2026-prestart-two-screen-delta.

   ONE component, two acts (D-AC-6: "Ein Vertrag, zwei Instanzen"). Act 1's
   stage belongs to VERA, act 2's to FENRIR; the stage itself does not change
   between them, only its focus does. That is how Richtung C's picture-stage
   language survives the two-screen split without dragging C's single-screen
   information architecture along with it.

   D-AC-3 — THE FRAME IS FIXED. '.prerun-heroframe' is a plain flex child whose
   box depends on nothing: not the focus, not the lock state, not the caption's
   text length, not the image aspect. Every rotary position is absolutely
   positioned inside it and only ever TRANSFORMED. This is the AC that closes
   the measured 19.9% / 52.9% hero-balance break, and it is closed by
   construction rather than by a CSS-text assertion — which is exactly the
   mistake that let the break ship.

   D-AC-4 — PROTECTION ZONE. The frame is the declared protected area
   ('data-protect="hero"'). No text node is mounted inside it; the caption is a
   sibling below. This is also why Richtung C's 3px text-stroke is gone: the
   spec predicted it (risk 3 — "fällt der Text von der Figur, entfällt die
   Ursache") and it did.
   ══════════════════════════════════════════════════════════════════════════ */
/* The layer that separates scenery from stage: darkens the edges, lifts a
   soft pool where the hero stands, and gives the whole picture one direction
   of light instead of an evenly lit composite. */
.prerun-depth {
  position:absolute; inset:0; z-index:0; pointer-events:none;
  background:
    radial-gradient(58% 74% at 67% 46%,rgba(150,215,235,.10),transparent 68%),
    linear-gradient(90deg,rgba(3,6,10,.80) 0%,rgba(3,6,10,.62) 22%,transparent 44%),
    radial-gradient(120% 120% at 50% 34%,transparent 38%,rgba(3,6,10,.62) 100%),
    linear-gradient(180deg,rgba(3,6,10,.52) 0%,transparent 26%,transparent 62%,rgba(3,6,10,.72) 100%);
}
.prerun-herozone {
  position:relative; flex:1 1 auto; min-height:0;
  display:flex; flex-direction:column; align-items:stretch;
  /* The header floats over the top of the stage, so the zone only has to clear
     the part of it that carries ink. Everything else — caption, jump targets —
     now lives INSIDE the frame's negative space rather than in bands beneath
     it, which is where the hero's size came from. */
  padding:${UI_SPACE.md}px 0 0; box-sizing:border-box;
  touch-action:pan-y; -webkit-tap-highlight-color:transparent;
}
/* The core's light — Richtung C's seam glow, re-aimed. On a single-hero stage
   there is no seam to split (that line existed BETWEEN two figures), so the
   light is what the hero stands in rather than what divides two of them. */
.prerun-corelight {
  position:absolute; left:50%; top:0; bottom:0; width:74%; margin-left:-37%;
  z-index:0; pointer-events:none; filter:blur(3px);
  background:
    radial-gradient(34% 42% at 50% 48%,color-mix(in srgb,var(--hero-ac,${G}) 15%,transparent),transparent 78%),
    radial-gradient(12% 14% at 50% 48%,rgba(200,238,255,.18),transparent 76%);
}
.prerun-rim { position:absolute; top:0; bottom:0; width:38%; z-index:0; pointer-events:none; }
.prerun-rim.l { left:0;  background:linear-gradient(90deg,color-mix(in srgb,var(--hero-ac,${G}) 16%,transparent),transparent 70%); }
.prerun-rim.r { right:0; background:linear-gradient(270deg,color-mix(in srgb,var(--hero-ac,${G}) 16%,transparent),transparent 70%); }

/* THE FIXED RECTANGLE (D-AC-3). */
.prerun-heroframe {
  position:relative; z-index:1; flex:1 1 auto; width:100%; min-height:0;
  outline:none; touch-action:pan-y;
  /* The frame CROPS. The active figure is deliberately taller than the frame
     so she reads at a size a landscape phone can actually resolve — face,
     cyberware and role silhouette — and is cut at the thigh rather than shrunk
     to fit whole. That is the difference between a character select and a
     thumbnail: measured before this change, VERA painted 158 x 237 px, i.e.
     19% of the screen's WIDTH, which is why she read as small even at 60% of
     its height. */
  overflow:hidden;
}
.prerun-heroframe:focus-visible { box-shadow:inset 0 0 0 1px ${G}; border-radius:10px; }

/* ── The rotary positions ────────────────────────────────────────────────────
   D-AC-8's staggering lives half here and half in applyRotary(): the per-item
   transform/opacity/z come from the index (deterministic, no measured layout,
   identical in jsdom and Chromium), and the material below is what makes the
   active one read as PRESENT and the neighbours as pushed back.

   The ratios, written down where they can be checked: active/neighbour
   1.00/0.62 = 1.61 (open upward — the coverflow language D-AC-8 asks for);
   neighbour/neighbour 0.62/0.56 = 1.107, i.e. 10.7%, inside the ≤12% cap that
   keeps the reel calm instead of tipping into a card effect. */
.prerun-rot {
  /* Taller than the frame (142%) and narrower (34%): with object-fit:contain
     the SHORTER edge decides, so a box taller than it is wide makes the WIDTH
     the limit and the figure paints at the box's full width. The overflow is
     cropped by the frame. --reel-cx lets act 1 push the reel right so VERA and
     her identity block compose together (D-AC-6 allows the two acts to look
     different; it requires a shared grammar, not a shared picture). */
  position:absolute; left:var(--reel-cx,50%); top:0; height:142%; width:34%;
  transform-origin:50% 0%; cursor:pointer;
  display:flex; align-items:flex-start; justify-content:center;
  transition:transform 320ms cubic-bezier(.22,.61,.36,1),
             opacity 320ms cubic-bezier(.22,.61,.36,1),
             filter 320ms cubic-bezier(.22,.61,.36,1);
  -webkit-tap-highlight-color:transparent;
  /* DEVICE ROUND 2026-08-18 (owner: swipe dead, only the arrows work): the
     effective touch-action is resolved along the TARGET's chain — the
     ancestor herozone's pan-y alone left the transformed/composited rotary
     items ambiguous on WebKit, so declare it where the finger actually
     lands. Horizontal motion stays with the pointer handlers (the swipe),
     vertical stays native. */
  touch-action:pan-y;
}
.prerun-rot-img {
  display:block; width:100%; height:100%;
  object-fit:contain; object-position:center top;
  position:relative; z-index:1;
  /* iOS: the native image-drag/save gesture must never intercept the reel
     swipe (device round 2026-08-18). */
  -webkit-user-drag:none; user-select:none; -webkit-user-select:none;
  /* The bottom of the crop dissolves instead of being scissored: a figure cut
     by a hard horizontal line reads as a mistake, a figure that fades into the
     dark reads as depth. */
  -webkit-mask-image:linear-gradient(180deg,#000 76%,rgba(0,0,0,.45) 92%,transparent 100%);
  mask-image:linear-gradient(180deg,#000 76%,rgba(0,0,0,.45) 92%,transparent 100%);
  filter:drop-shadow(0 0 30px rgba(0,0,0,.9));
}
/* DEVICE ROUND 2026-08-18 ("transparenter Skin", finding 6): the build-106
   runtime underlay (a second img through an SVG feFuncA solidifier +
   brightness(0)) is REMOVED — on the owner's device the filter reference
   silently did nothing (hair stayed see-through), and in browsers where it
   worked its slope-24 black silhouette backed every soft anti-aliased edge
   with hard black. The real fix is baked into the ONE affected asset:
   ghost.webp is interior-opaque (owner-ordered single-asset override of the
   asset freeze; 3x3-neighbourhood interior test, silhouette rim untouched —
   see the device-fix ledger). The other operator skins were judged fine on
   device as shipped. */
/* D-AC-4's declared protection zone. Purely a measurement surface: no paint, no
   hit-testing, no effect on layout. Its job is to describe the part of the
   position that the frame actually SHOWS — 1/1.42 of the host at scale 1, more
   as the position recedes (the scale runs from the top edge, so a smaller box
   fits inside the frame entirely). The 142 lives in one place, .prerun-rot's
   height, and this reads it back out; if that oversize ever changes, change it
   here in the same breath. */
.prerun-rot-prot {
  position:absolute; left:0; right:0; top:0;
  bottom:max(0%, calc(100% - 70.42% / var(--rot-s,1)));
  pointer-events:none; z-index:0;
}
/* The backlight plate each position stands against — its own light, sized to
   its own box, so a receding position dims with its light rather than just
   turning transparent. */
.prerun-rot-back {
  position:absolute; left:6%; right:6%; top:6%; height:66%; z-index:-1;
  border-radius:50%; filter:blur(16px);
  background:radial-gradient(closest-side,
    color-mix(in srgb,var(--rot-ac,${G}) 26%,transparent),transparent 76%);
  /* Slower than the figure it sits behind, so the light LAGS the move. That
     lag is the parallax: two things at different depths cannot settle at the
     same instant. */
  transition:opacity 560ms cubic-bezier(.22,.61,.36,1),
             transform 560ms cubic-bezier(.22,.61,.36,1);
}
/* The next position gets its own lamp and its own separation from the ground.
   Scale is untouched — D-AC-8's neighbour-to-neighbour cap is the scale rule —
   so the presence has to come from light, which is also the more convincing
   place for it to come from. */
.prerun-rot.nxt .prerun-rot-back {
  left:0; right:0; top:2%; height:74%;
  background:radial-gradient(closest-side,
    color-mix(in srgb,var(--rot-ac,${G}) 40%,transparent),transparent 78%);
}
.prerun-rot.nxt .prerun-rot-img {
  filter:
    blur(var(--rot-blur,0px)) saturate(var(--rot-sat,1)) brightness(var(--rot-bri,1))
    drop-shadow(0 0 12px color-mix(in srgb,${G} 20%,transparent))
    drop-shadow(0 0 34px rgba(0,0,0,.95));
}
/* CYAN RIM. drop-shadow follows the alpha channel, so four tight offsets in
   the accent trace the silhouette itself — a rim light, not a glow box. Only
   the active position carries it; that is what isolates her. */
.prerun-rot.on .prerun-rot-img {
  filter:
    brightness(1.07) contrast(1.07) saturate(1.04)
    drop-shadow(-1px 0 0 color-mix(in srgb,${GH} 52%,transparent))
    drop-shadow(0 -1px 0 color-mix(in srgb,${GH} 38%,transparent))
    drop-shadow(0 0 9px color-mix(in srgb,${G} 22%,transparent))
    drop-shadow(0 0 46px rgba(0,0,0,.95));
}
/* The rim EASES in on a change instead of appearing with the frame. Free —
   it rides the same transition the figure already has. */
.prerun-rot-img { transition:filter 380ms cubic-bezier(.22,.61,.36,1); }
/* Distance reads as ATMOSPHERE, not as opacity alone: the further back a
   position is, the more the air between takes its focus, its colour and its
   light. Set from the index in applyRotary so it stays deterministic. */
.prerun-rot:not(.on) .prerun-rot-img {
  filter:
    blur(var(--rot-blur,0px))
    saturate(var(--rot-sat,1))
    brightness(var(--rot-bri,1))
    drop-shadow(0 0 26px rgba(0,0,0,.9));
}
/* Standing light in the position's OWN colour, plus a contact shadow. This is
   the difference between a figure standing in the room and a sticker lying on
   it — and it is pure light, no asset involved. */
.prerun-rot::before {
  content:""; position:absolute; left:-20%; right:-20%; top:2%; height:76%;
  z-index:-1; filter:blur(8px);
  background:radial-gradient(closest-side,
    color-mix(in srgb,var(--rot-ac,${G}) 24%,transparent),transparent 74%);
}
/* No contact shadow: the figure is cropped at the thigh, so there is no floor
   for her to meet. Faking one under a cut-off body reads as a mistake. */
/* Locked reads as DRAINED, never as greyed-out UI: shape and saturation and
   text, never colour alone (§23 / AC-12). The figure stays fully present. */
.prerun-rot.on.locked .prerun-rot-img { filter:grayscale(.94) brightness(.5) contrast(.9) drop-shadow(0 0 30px rgba(0,0,0,.9)); }
.prerun-rot:not(.on).locked .prerun-rot-img { filter:blur(var(--rot-blur,0px)) grayscale(1) brightness(.44); }
/* Owner finding 7 (device round 2026-08-18): the 🔒 sits ON the locked figure
   while it is centred — upper-chest height, clear of the face — and the
   figure itself is veiled (applyRotary lowers --rot-sat/--rot-bri for
   .on.locked). The caption's lock line keeps carrying the unlock CONDITION;
   this badge carries the STATE. */
.prerun-rot-lockbadge {
  display:none; position:absolute; left:50%; top:30%;
  transform:translate(-50%,-50%);
  z-index:3; pointer-events:none; line-height:1;
  font-size:${roleFontSize("display")};
  filter:drop-shadow(0 2px 10px rgba(0,0,0,.95));
  opacity:.92;
}
.prerun-rot.on.locked .prerun-rot-lockbadge { display:block; }


/* ── The caption — OUTSIDE the protection zone (D-AC-4) ───────────────────── */
/* The identity block sits IN the stage's negative space, not in a band under
   the picture: figure and information compose together, which is what the
   band could never do. It is anchored left and vertically centred, and its
   width stops well short of the reel so no text ever crosses the figure —
   D-AC-4 is a geometry property here, not a promise. */
.prerun-herocap {
  position:absolute; left:0; top:50%; transform:translateY(-50%);
  z-index:120; width:34%; max-width:34%;
  display:flex; flex-direction:column; align-items:flex-start; gap:${
    UI_SPACE.hair
  }px;
  /* DEVICE ROUND 2026-08-18 (owner findings 2/8): in landscape the Dynamic
     Island sits exactly here — LEFT edge, vertically centred — and the
     identity block (SENTINEL-7 / CONTAINMENT etc.) ran under it. The text
     inset yields to the real per-side safe-area (published as
     --inset-left-design by index.html, same pattern as HUD/EnemyLibrary);
     off-notch devices keep the designed inset unchanged. */
  padding:${UI_SPACE.md}px 0 ${UI_SPACE.md}px
    max(calc(${UI_SPACE.xl}px + ${UI_SPACE.xxl}px),
        calc(var(--inset-left-design, 0px) + ${UI_SPACE.md}px));
  box-sizing:border-box; text-align:left; pointer-events:none;
}
/* The block is ANCHORED to the stage rather than boxed off from it: one
   hairline down its left edge, brightest where the type is and fading out at
   both ends, plus a tick at its head. No panel, no fill — the negative space
   around the type does the separating. */
.prerun-herocap::before {
  content:""; position:absolute; top:0; bottom:0; width:1px;
  left:max(${UI_SPACE.xxl}px, var(--inset-left-design, 0px));
  background:linear-gradient(180deg,transparent,color-mix(in srgb,${G} 62%,transparent) 22%,
             color-mix(in srgb,${G} 62%,transparent) 74%,transparent);
}
.prerun-herocap::after {
  content:""; position:absolute; top:20%;
  left:calc(max(${UI_SPACE.xxl}px, var(--inset-left-design, 0px)) - 3px);
  width:7px; height:1px; background:${G}; box-shadow:0 0 6px ${G};
}
/* The one line that ties the block to the stage: it leaves the rule under the
   role and runs toward the figure, fading out long before it reaches her. No
   information, no arrow, no label — just a direction. */
.prerun-caprule::after {
  content:""; position:absolute; left:100%; top:50%; width:280px; height:1px;
  background:linear-gradient(90deg,color-mix(in srgb,${G} 46%,transparent),transparent 88%);
  pointer-events:none;
}
.prerun-caprule { position:relative; }
/* The rule under the role — the reference's short cyan dash. It separates the
   identity (who) from the effects (what she does) without a second label. */
.prerun-caprule {
  width:34px; height:2px; margin:${UI_SPACE.xs}px 0;
  background:${G}; box-shadow:0 0 10px ${G}; border-radius:2px;
}
.prerun-herocap > * { pointer-events:auto; }
.prerun-herofx { display:flex; flex-direction:column; gap:0; max-width:100%; }
.prerun-fxline {
  font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "body"
  )}; letter-spacing:.04em;
  color:${GH}; line-height:1.35; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
}
.prerun-heroname {
  font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "display"
  )}; letter-spacing:.06em;
  line-height:1.15; color:var(--rar-color,${GH}); white-space:nowrap;
  display:flex; align-items:baseline; gap:${UI_SPACE.sm}px; max-width:100%;
  text-shadow:0 0 20px color-mix(in srgb,var(--rar-color,${GH}) 40%,transparent);
}
.prerun-heroname.locked { color:${STEEL}; text-shadow:none; }
/* The crest reads as a BADGE beside the name, not as a bullet in front of it. */
.prerun-crest {
  display:inline-flex; align-items:center; justify-content:center;
  width:1.55em; height:1.55em; flex:0 0 auto; margin-right:${UI_SPACE.xs}px;
  font-size:${roleFontSize(
    "heading"
  )}; color:var(--rar-color,${G}); line-height:1;
  border:1px solid color-mix(in srgb,var(--rar-color,${G}) 55%,transparent);
  border-radius:6px 6px 9px 9px;
  background:color-mix(in srgb,var(--rar-color,${G}) 10%,transparent);
  filter:drop-shadow(0 0 8px color-mix(in srgb,var(--rar-color,${G}) 45%,transparent));
}
.prerun-herover { color:${DIM}; font-size:${roleFontSize(
    "body"
  )}; letter-spacing:.02em; }
.prerun-heroline {
  font-size:${roleFontSize(
    "body"
  )}; color:${STEEL}; line-height:1.3; letter-spacing:.08em;
  max-width:100%; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
}
/* D-AC-3 — every caption line keeps its line box in ALL states. The lock line
   is present on an unlocked option too, holding a NBSP: if it collapsed, the
   hero frame's flex share would grow and the fixed-geometry AC would break on
   nothing more than which option happens to be focused (measured before this
   rule: 142.35 vs 102.41 px frame height across the five operators). */
.prerun-herolock {
  font-size:${roleFontSize(
    "body"
  )}; color:${GOLDK}; letter-spacing:.08em; line-height:1.3;
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:100%;
  min-height:1.3em;
}
.prerun-heroline, .prerun-heroname { min-height:1.3em; }

/* ── Jump targets (AC-9(4), kept) ────────────────────────────────────────────
   The rotary's own back positions are small and get smaller at the ends of the
   reel, so they cannot be the only way to reach a distant option on a phone.
   Each pip carries a real touch-floor hit area (D-AC-18) with the small mark
   drawn inside it. */
/* A single key light behind the hero's head and shoulders. It is what makes
   her separate from the ground without a cut-out edge — local, not global. */
/* The key light is TIGHT on head and shoulders and the ground around it is
   pushed down, because face, black suit and background were sitting in the
   same tonal band — that is what kept the figure in the scene instead of
   letting her step out of it. The dark ring does as much work as the light. */
.prerun-keylight {
  position:absolute; left:var(--reel-cx,50%); top:2%; width:46%; height:78%;
  margin-left:-23%; z-index:0; pointer-events:none; filter:blur(2px);
  transform:translateX(var(--reel-drift,0%));
  transition:transform 620ms cubic-bezier(.22,.61,.36,1);
  background:
    radial-gradient(26% 22% at 50% 20%,rgba(196,242,236,.34),transparent 74%),
    radial-gradient(42% 36% at 50% 30%,rgba(150,214,224,.20),transparent 76%),
    radial-gradient(76% 68% at 50% 56%,rgba(6,12,20,.66),transparent 76%);
  animation:prerun-breathe 9s ease-in-out infinite;
}
/* 0.11 Hz — an order of magnitude under the charter's 1 Hz ceiling for
   sustained pulses, and amplitude low enough that it reads as a room, not as
   a pulse. */
@keyframes prerun-breathe {
  0%,100% { opacity:.88; }
  50%     { opacity:1; }
}
/* Grain + scanline, at the threshold of visible. This is the difference
   between a flat composite and a surface that was photographed. */
.prerun-grain {
  position:absolute; inset:0; z-index:110; pointer-events:none; opacity:.5;
  background:
    repeating-linear-gradient(0deg,rgba(255,255,255,.014) 0 1px,transparent 1px 3px),
    repeating-linear-gradient(90deg,rgba(136,224,208,.010) 0 1px,transparent 1px 4px);
  mix-blend-mode:screen;
}
/* Corner ticks — a technical frame that states its edges without boxing the
   picture in. Frame interruptions, not a border. */
.prerun-ticks { position:absolute; inset:0; z-index:112; pointer-events:none; }
.prerun-ticks i {
  position:absolute; width:15px; height:15px; opacity:.5;
  border:1px solid ${G}; border-right:0; border-bottom:0;
}
.prerun-ticks .tl { left:0; top:0; }
.prerun-ticks .tr { right:0; top:0; transform:rotate(90deg); }
.prerun-ticks .br { right:0; bottom:0; transform:rotate(180deg); }
.prerun-ticks .bl { left:0; bottom:0; transform:rotate(270deg); }

/* AC-9(4) — the step arrows flank the stage at mid height, clear of the
   figure and clear of the identity block. */
.prerun-rot-arw {
  position:absolute; top:50%; transform:translateY(-50%); z-index:120;
  width:${PRERUN_TOUCH_FLOOR}; height:${PRERUN_TOUCH_FLOOR}; padding:0;
  display:flex; align-items:center; justify-content:center; cursor:pointer;
  background:none; border:none; transition:opacity .15s,transform .15s;
  -webkit-tap-highlight-color:transparent;
}
/* Drawn, not typeset — see the note in PreRunScreen.buildHeroStage. Sized off
   the button's own box, so it scales with the touch floor rather than with the
   type scale. */
.prerun-rot-arw i {
  display:block; width:34%; height:34%; border:2px solid ${G}; border-right:0; border-bottom:0;
  filter:drop-shadow(0 0 8px color-mix(in srgb,${G} 60%,transparent));
}
.prerun-rot-arw.prev i { transform:rotate(-45deg) translate(8%,8%); }
.prerun-rot-arw.next i { transform:rotate(135deg) translate(8%,8%); }
/* Flanking the reel, not floating in the middle of it: the identity block owns
   the left third, so the back arrow sits at its edge and the forward arrow at
   the stage's far side. */
.prerun-rot-arw.prev { left:34%; }
.prerun-rot-arw.next { right:1%; }
.prerun-rot-arw:hover i { border-color:${GH}; }
.prerun-rot-arw:active { transform:translateY(-50%) scale(.9); }
.prerun-rot-arw.off { opacity:.32; cursor:not-allowed; }
.prerun-rot-arw.off i { border-color:${GDK}; filter:none; }

/* The jump targets sit centred beneath the figure, as in the reference. */
.prerun-pips {
  position:absolute; left:50%; bottom:0; z-index:120;
  transform:translateX(-50%);
  display:flex; align-items:center; pointer-events:none;
}
.prerun-pip { pointer-events:auto; }
.prerun-pip {
  width:${PRERUN_TOUCH_FLOOR}; height:${PRERUN_TOUCH_FLOOR};
  padding:0; border:none; background:none; cursor:pointer;
  display:flex; align-items:center; justify-content:center;
  -webkit-tap-highlight-color:transparent;
}
.prerun-pip i {
  display:block; width:9px; height:9px; border-radius:6px;
  background:rgba(136, 224, 208,.26); transition:width 280ms cubic-bezier(.22,.61,.36,1);
}
/* The dots stay dots. The MARK is a single element that slides between them,
   driven by --pip-i and the pip's own fixed width — deterministic, no
   measurement, no loop. */
.prerun-pip.on i { background:color-mix(in srgb,${G} 34%,transparent); }
.prerun-piprunner {
  position:absolute; left:0; top:50%; margin-top:-2px; z-index:1;
  width:${PRERUN_TOUCH_FLOOR}; height:4px; pointer-events:none;
  transform:translateX(calc(var(--pip-i,0) * ${PRERUN_TOUCH_FLOOR}));
  transition:transform 380ms cubic-bezier(.22,.61,.36,1);
}
.prerun-piprunner::before {
  content:""; position:absolute; left:50%; top:0; width:26px; height:4px;
  margin-left:-13px; border-radius:3px;
  background:${G}; box-shadow:0 0 12px ${G};
}

/* A locked option reads as an empty ring — visible, reachable, not yours. */
.prerun-pip.lk i { background:transparent; border:1px solid ${GOLDK}; }
.prerun-pip.lk.on i { background:${GOLDK}; border-color:${GOLDK}; box-shadow:0 0 12px ${GOLDK}; }

/* ── Act header: the step number + the act's own name ────────────────────── */
.prerun-act-step {
  font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "body"
  )}; line-height:1;
  color:${GH}; border:1px solid rgba(136, 224, 208,.42); border-radius:4px;
  padding:2px 7px; letter-spacing:0;
}
.prerun-act-title {
  font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "heading"
  )}; letter-spacing:.22em;
  color:${GH}; line-height:1.1; text-shadow:0 0 12px rgba(136, 224, 208,.4);
  overflow:hidden; text-overflow:ellipsis;
}

/* ── Act 2's threat-context surface + act 2's read-only summary ──────────── */
.prerun-threatctx { flex:0 0 auto; }
.prerun-threatctx-body { min-width:0; display:flex; align-items:center; gap:${
    UI_SPACE.md
  }px; padding:${UI_SPACE.xxs}px ${UI_SPACE.lg}px; }
.prerun-ctx-name {
  font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "heading"
  )}; letter-spacing:.1em;
  color:var(--tc,${GH}); line-height:1.1; white-space:nowrap;
  text-shadow:0 0 14px color-mix(in srgb,var(--tc,${G}) 40%,transparent);
}
.prerun-ctx-lock {
  font-size:${roleFontSize("body")}; color:${GOLDK}; letter-spacing:.06em;
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
}
.prerun-ctx-lock[hidden] { display:none; }
.prerun-console-spacer { flex:1 1 auto; min-width:0; }
/* D-AC-15 — read-only. There is deliberately no control in here. */
.prerun-summary {
  display:flex; align-items:center; gap:${UI_SPACE.xl}px;
  flex:1 1 auto; min-width:0; overflow:hidden;
}
.prerun-sum-cell { display:flex; flex-direction:column; gap:0; min-width:0; flex:0 1 auto; overflow:hidden; }
.prerun-sum-k { font-size:${roleFontSize(
    "body"
  )}; letter-spacing:.2em; color:${DIM}; line-height:1.2; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.prerun-sum-v {
  font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "body"
  )}; letter-spacing:.04em;
  color:${GH}; line-height:1.2; white-space:nowrap;
  overflow:hidden; text-overflow:ellipsis;
}
.prerun-sum-v.short { color:${COLORS.AMBER_WARN}; }
/* The loadout is a GROUP inside the deployment console now, not a band of its
   own — the owner's note that ID left and CONTINUE right left "große tote
   Flächen" between them. Slots, identity and the primary action share one row
   and read as one instrument. */
.prerun-loadout { flex:0 0 auto; min-width:0; display:flex; flex-direction:column; gap:${
    UI_SPACE.hair
  }px; }
.prerun-console-identity-zone { flex:0 0 auto; min-width:0; display:flex; flex-direction:column; gap:${
    UI_SPACE.hair
  }px; }
/* The reference's "— LABEL" section caption. */
.prerun-grouplab {
  display:flex; align-items:center; gap:${UI_SPACE.xs}px;
  font-size:${roleFontSize(
    "body"
  )}; letter-spacing:.22em; color:${GDK}; line-height:1.2;
  white-space:nowrap;
}
.prerun-grouplab::before {
  content:""; width:12px; height:1px; background:${GDK}; flex:0 0 auto;
}
.prerun-modcount {
  font-size:${roleFontSize(
    "body"
  )}; letter-spacing:.14em; color:${DIM}; line-height:1.2;
  white-space:nowrap;
}
.prerun-modcount.short { color:${COLORS.AMBER_WARN}; }
.prerun-id-crest {
  display:inline-flex; align-items:center; justify-content:center;
  width:1.5em; height:1.5em; flex:0 0 auto;
  font-size:${roleFontSize("heading")}; color:${G}; line-height:1;
  border:1px solid rgba(136, 224, 208,.5); border-radius:5px 5px 8px 8px;
  background:rgba(136, 224, 208,.10);
}
.prerun-id-pen { font-size:${roleFontSize(
    "body"
  )}; color:${G}; opacity:.8; flex:0 0 auto; }



/* ── LOADOUT MODULES — horizontal scroller (owner): ~5 premium cards visible,
   swipe/scroll for the rest (12). Card = hex icon · name · category · desc · ✓/lock. */
.prerun-module-section { border-top:1px dotted rgba(136, 224, 208,.18); margin-top:2px; }
.prerun-mod-count {
  text-align:center; color:${DIM}; font-family:${UI_FONT_STACK};
  font-size:${roleFontSize("body")}; letter-spacing:.2em; padding-top:${
    UI_SPACE.xs
  }px;
}
.prerun-mod-count.active { color:${G}; }
.prerun-mod-count.full { color:${GH}; text-shadow:0 0 8px rgba(136, 224, 208,.5); }
.prerun-mod-count.full-flash { animation:prerun-modfull .5s ease-out 1; }
@keyframes prerun-modfull {
  0%,100%{color:${G}} 30%{color:${
    COLORS.AMBER_WARN
  };text-shadow:0 0 10px rgba(232, 208, 152,.7)}
}
/* the strip: a horizontal flex scroller; EXACTLY 5 cards span the width, rest scroll */
.prerun-modules {
  display:flex; flex-wrap:nowrap; gap:${UI_SPACE.md}px; padding:${
    UI_SPACE.xs
  }px ${UI_SPACE.lg}px ${UI_SPACE.xs}px;
  overflow-x:auto; overflow-y:hidden; scroll-snap-type:x proximity; scroll-behavior:smooth;
  scrollbar-width:thin; scrollbar-color:rgba(136, 224, 208,.4) transparent;
}
.prerun-modules::-webkit-scrollbar { height:6px; }
.prerun-modules::-webkit-scrollbar-thumb { background:rgba(136, 224, 208,.35); border-radius:3px; }
.prerun-modules::-webkit-scrollbar-track { background:rgba(136, 224, 208,.06); border-radius:3px; }
.prerun-modc {
  position:relative; flex:0 0 calc((100% - ${
    UI_SPACE.md * 4
  }px) / 5); box-sizing:border-box;
  scroll-snap-align:start; display:flex; flex-direction:row; align-items:center; gap:${
    UI_SPACE.md
  }px;
  border:1px solid ${PB}; border-radius:10px; padding:${
    UI_SPACE.md
  }px; overflow:hidden; cursor:pointer;
  background:linear-gradient(180deg,${GT},${GB}); transition:border-color .15s,box-shadow .15s;
}
/* hexagonal icon plate */
.modc-hex {
  position:relative; flex-shrink:0; width:56px; height:62px; display:flex; align-items:center; justify-content:center;
  clip-path:polygon(50% 0,100% 25%,100% 75%,50% 100%,0 75%,0 25%);
  background:rgba(136, 224, 208,.08); transition:background .15s;
}
.modc-ic { width:32px; height:32px; object-fit:contain; filter:drop-shadow(0 0 5px rgba(136, 224, 208,.5)); }
.modc-txt { display:flex; flex-direction:column; gap:2px; min-width:0; flex:1; }
/* AC-26 — neither line declared a wrap guard (no nowrap/ellipsis either), so
   a name/category wider than the card's own compact column (min-width:0 on
   .modc-txt lets it shrink there) genuinely clipped instead of wrapping —
   measured on "OVERCLOCKED BOOT"/"WIRTSCHAFT" et al., 8-36 design-px over.
   The readability-net wrap-guard idiom this repo already uses elsewhere
   (tests/ui/readabilityLayoutNet.test.ts's WRAP GUARD invariant) lets the
   card grow a line taller instead of losing the tail of a word. */
.modc-name { font-size:${roleFontSize(
    "body"
  )}; letter-spacing:.05em; color:${STEEL}; line-height:1.1; overflow-wrap:break-word; word-break:normal; }
.modc-cat { font-size:${roleFontSize(
    "body"
  )}; letter-spacing:.16em; color:${GD}; overflow-wrap:break-word; word-break:normal; }
.modc-desc { font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "body"
  )}; color:${DIM}; letter-spacing:.02em; line-height:1.25; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
/* ✓ check — revealed on the selected card */
.modc-check {
  position:absolute; top:7px; right:7px; width:20px; height:20px; border-radius:5px; display:none;
  align-items:center; justify-content:center; font-size:${roleFontSize(
    "body"
  )}; color:${GT};
  background:${G}; box-shadow:0 0 10px -2px rgba(136, 224, 208,.85);
}
.prerun-modc:not(.sel):not(.locked):hover { border-color:rgba(136, 224, 208,.5); box-shadow:0 0 16px -6px rgba(136, 224, 208,.45); }
.prerun-modc.sel {
  border-color:${G};
  box-shadow:0 0 18px -5px rgba(136, 224, 208,.5),inset 0 0 18px -9px rgba(136, 224, 208,.55);
  background:radial-gradient(120% 120% at 50% 0%,rgba(136, 224, 208,.12),${GB});
}
.prerun-modc.sel .modc-name { color:${GH}; }
.prerun-modc.sel .modc-hex { background:rgba(136, 224, 208,.18); }
.prerun-modc.sel .modc-check { display:flex; }
/* locked — a padlock in the hex, the card dimmed/receded */
.prerun-modc.locked { cursor:not-allowed; opacity:.5; }
.prerun-modc.locked .modc-ic { opacity:.3; filter:grayscale(.5); }
.modc-lock { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; font-size:${roleFontSize(
    "title"
  )}; filter:drop-shadow(0 0 6px rgba(0,0,0,.7)); }
.prerun-modc:not(.locked) .modc-lock { display:none; }

/* ── Step badges ────────────────────────────────────────────────────── */
/* AC-26 — same box-vs-glyph scaling gap as .mc-crest above (measured 20 vs
   16 design-px), same fix: withUiScale() on the box + line-height:1. */
.prerun-step {
  display:inline-flex; align-items:center; justify-content:center;
  width:${withUiScale(18)}; height:${withUiScale(
    18
  )}; border-radius:50%; border:1px solid ${G}; color:${G};
  font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "body"
  )}; line-height:1; margin-right:5px; vertical-align:-3px; flex-shrink:0;
}

/* ── Deploy glitch-out (preserved from original) ─────────────────────── */
.prerun-deploy-glitch {
  position:absolute; inset:0; z-index:200; pointer-events:none;
  animation:deploy-cut 0.8s steps(1) forwards;
}
@keyframes deploy-cut {
  0%{background:transparent;} 5%{background:#000;} 8%{background:transparent;}
  12%{background:#000;} 15%{background:rgba(136, 224, 208,0.05);} 18%{background:#000;}
  25%{background:transparent;} 30%{background:#000;} 40%{background:transparent;}
  45%{background:#0a0a0a;} 55%{background:#000;} 100%{background:#000;}
}
.prerun-deploy-glitch.prerun-blackout { background:#000 !important; animation:none; }
.prerun-deploy-glitch::before {
  content:''; position:absolute; left:0; right:0; height:3px; top:30%;
  background:rgba(255,255,255,0.5); animation:deploy-tear 0.4s steps(3) forwards;
}
@keyframes deploy-tear { 0%{top:20%;opacity:1;} 50%{top:65%;opacity:0.4;} 100%{top:90%;opacity:0;} }
.prerun-shakeout { animation:prerun-shake 0.5s steps(2) forwards; }
@keyframes prerun-shake {
  0%{transform:translateX(0) skewX(0); filter:none;}
  10%{transform:translateX(10px) skewX(-2deg); filter:hue-rotate(30deg) brightness(0.3);}
  20%{transform:translateX(-8px) skewX(1.5deg); filter:hue-rotate(-20deg) brightness(1.5);}
  35%{transform:translateX(5px) skewX(-1deg); filter:brightness(0.5);}
  50%{transform:translateX(-3px); filter:brightness(0.2);}
  70%{transform:translateX(0); filter:brightness(0);}
  100%{transform:translateX(0); filter:brightness(0);}
}

/* BL-199 — L FIT reflow. At --ui-scale 1.25 (canonical) the bigger type made
   PreRun a scrolling column; these L rules FIT it to the stage (same layout,
   only the VERTICAL footprint densified via UI_SPACE + the big fixed blocks —
   never the type, fonts stay role-driven ≥12px). Summed height now < stage(900)
   − topbar(42) = 858px (docs/prerun_fit.html); overflow-y:auto = fallback. */
#pre-run-screen.prerun-scale-l .prerun-main { gap:${UI_SPACE.sm}px; padding:${
    UI_SPACE.xs
  }px ${UI_SPACE.xl}px; max-width:none; }
#pre-run-screen.prerun-scale-l .prerun-info-band { gap:${UI_SPACE.md}px; }
#pre-run-screen.prerun-scale-l .prerun-panel__body { padding:${UI_SPACE.xs}px ${
    UI_SPACE.lg
  }px; }
#pre-run-screen.prerun-scale-l .prerun-terminal { max-height:150px; }
#pre-run-screen.prerun-scale-l .prerun-vs-grid { padding:${UI_SPACE.xxs}px ${
    UI_SPACE.sm
  }px ${UI_SPACE.hair}px; gap:${UI_SPACE.xs}px; }
/* BL-202 — the avatar is now shown WHOLE (object-fit:contain, no crop) BESIDE the
   text; 78px reads the full character. Widening the faces collapsed VERA's tall
   stack, so the band still fits the 858px budget even at this taller avatar. */
#pre-run-screen.prerun-scale-l .prerun-face .operator-mascot { height:78px; }
#pre-run-screen.prerun-scale-l .prerun-face { padding:${UI_SPACE.xs}px ${
    UI_SPACE.sm
  }px; gap:${UI_SPACE.sm}px; }
#pre-run-screen.prerun-scale-l .prerun-face-body { gap:${UI_SPACE.hair}px; }
/* namecell fixed 54px doesn't scale with type → 48px ≥ 44px touch floor. */
#pre-run-screen.prerun-scale-l .prerun-namecell { height:48px; line-height:48px; }
#pre-run-screen.prerun-scale-l .prerun-gauge { padding-top:${UI_SPACE.hair}px; }
#pre-run-screen.prerun-scale-l .prerun-mc { min-height:0; }
#pre-run-screen.prerun-scale-l .prerun-model-section { margin:${
    UI_SPACE.xxs
  }px 0 ${UI_SPACE.hair}px; padding:${UI_SPACE.xxs}px ${UI_SPACE.lg}px ${
    UI_SPACE.xxs
  }px; }
#pre-run-screen.prerun-scale-l .prerun-modc { min-height:0; }

/* S188/BL-209 — Direction A: focus card + carousel + summoned loadout tray +
   imagery + new motion (class-gated reduced-motion; CRT atmosphere stays on). */

/* mainframe-core background plate (opaque webp; missing → transparent, §3). */
.prerun-bgplate {
  position:absolute; inset:0; z-index:0; pointer-events:none;
  background:url(/img/prerun/bg-core.webp) center center / cover no-repeat;
  /* The plate contains rectangular architecture, and at full sharpness those
     rectangles read as UI panels sitting BESIDE the hero rather than as a room
     BEHIND her — the "panel impression" on the left is this art, not a panel
     (probed with elementFromPoint: nothing there draws a box). Blur plus a
     desaturate/darken push it into depth-of-field, which is what makes it
     scenery. */
  filter:blur(2.4px) saturate(.5) brightness(.6) contrast(.95);
  transform:scale(1.06);
  opacity:.42;
  /* 74s for one pass. At this speed nothing is ever seen to move — the screen
     simply is not dead when you stop looking at it. Transform only, so it
     costs a composited layer and no layout. */
  animation:prerun-bgdrift 74s linear infinite alternate;
  -webkit-mask-image:radial-gradient(120% 100% at 50% 38%,rgba(0,0,0,1) 30%,rgba(0,0,0,.35) 100%);
  mask-image:radial-gradient(120% 100% at 50% 38%,rgba(0,0,0,1) 30%,rgba(0,0,0,.35) 100%);
}

/* (The operator THUMBNAIL grid, the expand-on-select FOCUS card, and the
   summoned LOADOUT slots/tray were retired — the SELECT OPERATOR swipe carousel
   above and the premium module-card row below replace them, S188.) */


/* ── READINESS BUS (Richtung C, §E/§F) ──────────────────────────────────────
   A circuit, not a progress bar.  Three nodes — one per deploy-gate condition,
   in the checklist's own order (① initials ② operator ③ modules) — joined by
   traces that conduct only while everything upstream holds.  The bus goes dark
   at exactly the first unmet condition, and the ONE need line beneath names it.
   That is why DEPLOY below reads as UNPOWERED rather than "greyed out": no
   current reaches it, and the player can see where the break is. */
.prerun-busstrip {
  display:flex; flex-direction:column; gap:4px; width:100%; min-width:0;
  /* The bus reads left-to-right across the console because that is the order
     the player fills it in: ID (far left, next to the chip) -> operator ->
     modules (next to the slots) -> DEPLOY. Boxed into the deploy column it
     read as a label ON the button instead of a route TO it. */
}
.prerun-bus { display:flex; align-items:center; width:100%; min-width:0; }
.prerun-bus-node { display:flex; align-items:center; gap:${
    UI_SPACE.hair
  }px; flex:0 1 auto; min-width:0; overflow:hidden; }
.prerun-bus-pip {
  width:8px; height:8px; border-radius:50%; flex:0 0 auto;
  background:rgba(136, 224, 208,.16); border:1px solid ${GDK};
  transition:background 220ms ease,box-shadow 220ms ease,border-color 220ms ease;
}
.prerun-bus-node.on .prerun-bus-pip { background:${G}; border-color:${G}; box-shadow:0 0 10px ${G}; }
.prerun-bus-lb {
  font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "body"
  )}; letter-spacing:.16em;
  color:${GDK}; line-height:1.2; white-space:nowrap; transition:color 220ms ease;
}
.prerun-bus-node.on .prerun-bus-lb { color:${G}; }
.prerun-bus-trace {
  flex:1 1 0; min-width:10px; height:1px; margin:0 ${UI_SPACE.xs}px;
  background:rgba(136, 224, 208,.14); position:relative; overflow:hidden;
}
.prerun-bus-trace.on { background:color-mix(in srgb,${G} 46%,transparent); }
/* The travelling spark that makes a live trace read as CARRYING rather than
   merely being lit.  Damped under reduced motion below — never frozen mid-run
   (a stopped spark reads as a fault). */
.prerun-bus-trace i {
  position:absolute; top:0; bottom:0; width:26%; opacity:0;
  background:linear-gradient(90deg,transparent,${GH},transparent);
}
.prerun-bus-trace.on i { opacity:1; animation:prerun-bus-spark 1800ms linear infinite; }
@keyframes prerun-bus-spark { from{transform:translateX(-120%)} to{transform:translateX(520%)} }
/* The ONE line that says what is missing (or that everything is ready). */
.prerun-needline {
  font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "body"
  )}; letter-spacing:.10em;
  color:${COLORS.AMBER_WARN}; line-height:1.3; min-height:16px;
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
}
.prerun-needline.ok { color:${GH}; text-shadow:0 0 10px rgba(136, 224, 208,.45); }

/* ── Boot-in reveal (move 2): the content stagger-fades on entry ────────── */
.prerun-bootin .prerun-info-band { animation:prerun-bootin-fade 420ms cubic-bezier(0.16,1,0.3,1) both; }
.prerun-bootin .prerun-main > .prerun-panel { animation:prerun-bootin-fade 420ms cubic-bezier(0.16,1,0.3,1) 90ms both; }
@keyframes prerun-bootin-fade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:none} }

/* reduced-motion: damp ONLY the new motion (class-driven via .prerun-rm set in
   show(); no OS media query here — the CRT/glitch atmosphere stays on).
   Task-009 (2026-prestart-deployment-redesign, AC-14) — .prerun-tr-tile
   joined this list: Task-003 shipped its own transition (filter/border-
   color/box-shadow/transform) without adding it here, so the threat rail's
   raise-on-select kept transitioning under reduced motion even though the
   operator carousel's equivalent motion already dropped away — both
   selectors now damp identically. */
#pre-run-screen.prerun-rm .prerun-rot,
#pre-run-screen.prerun-rm .prerun-rot-img,
#pre-run-screen.prerun-rm .prerun-rot-back,
#pre-run-screen.prerun-rm .prerun-bgplate,
#pre-run-screen.prerun-rm .prerun-keylight,
#pre-run-screen.prerun-rm .prerun-piprunner,
#pre-run-screen.prerun-rm .prerun-deploy-btn,
#pre-run-screen.prerun-rm .prerun-slot,
#pre-run-screen.prerun-rm .prerun-id-chip,
#pre-run-screen.prerun-rm .prerun-pip i,
#pre-run-screen.prerun-rm .prerun-cmd-dot,
#pre-run-screen.prerun-rm .prerun-cmd-back,
#pre-run-screen.prerun-rm .prerun-bus-pip,
#pre-run-screen.prerun-rm .prerun-bus-lb,
#pre-run-screen.prerun-rm .prerun-info-band,
#pre-run-screen.prerun-rm .prerun-main > .prerun-panel {
  animation:none !important; transition:none !important;
}
/* The travelling bus spark is the one case where "stop the animation" is the
   WRONG damping: a spark frozen mid-trace reads as a stuck circuit, i.e. as a
   fault the player did not cause.  Under reduced motion the trace keeps its
   lit/dark state and simply loses the traveller. */
#pre-run-screen.prerun-rm .prerun-bus-trace i { animation:none !important; opacity:0 !important; }

/* (Operator art = VERA augmentation variants — the per-card portrait sizing now
   lives in the carousel block above; the thumbnail/focus rules were retired.) */

/* Task-001 (2026-prestart-deployment-redesign, AC-8) — the four hardcoded
   9/7/9/8 FENRIR stat bars that used to live here (.prerun-enemy-stats /
   .prerun-statbar) are removed outright; the ENEMY INTEL button below now
   carries the margin-top:auto that used to pin the stat bars to the bottom
   of the card. */

/* ════════════════════════════════════════════════════════════════════════
   S188 RECOMPOSE — the owner's ChatGPT mockup layout: a 3-column command row
   (MISSION BRIEFING · SELECT OPERATOR · ENEMY ANALYSIS), a centered DEPLOY
   console, and the LOADOUT module-card row.
   ════════════════════════════════════════════════════════════════════════ */
/* BL-224 — the command row now GROWS into the height reclaimed from the one-row
   deploy console, so the VERA carousel + FENRIR panel read bigger. */
/* The hero is a STAGE, not a grid of panels — its geometry is defined in the
   HERO STAGE block above, next to the figures and the seam it exists for.
   Task-001's '1fr 1fr' two-panel hero and the separate '.prerun-mission' band
   are gone (Richtung C, docs/prerun_c_gap_2026-08.md §A/§B); the mission text
   below now renders inside '.prerun-cmd-mission', over the picture. */

/* The THREAT RAIL is gone with the single-screen architecture: its five flat
   tiles were the abgelöste Trägerform (spec §1, AC-15 -> D-AC-7). Act 2's
   threat selector is the SAME rotary act 1 uses — that is the point of D-AC-6,
   and it is also what fixed the deeper complaint the spec names: the operator
   was a coverflow while the threat was a flat tile strip, i.e. "zwei
   verschiedene Interaktionssprachen für dieselbe Art Entscheidung". The tier
   colours, the per-tier crop anchor and the two lock languages all survive on
   the rotary; only the tile chrome is gone. */

/* AC-17 (KEEP) on the two-act stage. Both hero names used to read over
   character art, which is what the retired '.face-name' plate and the 3px
   text-stroke existed for. D-AC-4 moved every text node OUT of the hero frame,
   so the caption now sits on ordinary panel ground below the figure — the
   spec called this outcome in advance (risk 3: "fällt der Text von der Figur,
   entfällt die Ursache"). The requirement is unchanged; its cause is gone.

/* ENEMY INTEL button + the FENRIR dossier popover it reveals. Task-001 —
   margin-top:auto now pins it to the card's bottom (the role the removed
   stat bars used to play, AC-8). */
.prerun-enemy-intel { position:relative; z-index:1; flex:0 0 auto; }
.prerun-intel-btn {
  /* D-AC-18 — measured 102x21 real px on act 2 before this: a bare padding
     value inside the scaled stage does not reach the touch floor. Same trap as
     the gear, same fix, and it only surfaced once this control became a
     first-class element of a screen instead of a corner of a retired panel. */
  min-height:${PRERUN_TOUCH_FLOOR};
  display:inline-flex; align-items:center; justify-content:center;
  width:auto; white-space:nowrap; cursor:pointer; font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "body"
  )};
  letter-spacing:.2em; padding:${
    UI_SPACE.sm
  }px; border-radius:8px; color:${CRIM};
  background:rgba(232, 154, 184,.06); border:1px solid rgba(232, 154, 184,.28); transition:all .15s;
}
.prerun-intel-btn:hover { border-color:${CRIM}; color:${GH}; box-shadow:0 0 12px -4px rgba(232, 154, 184,.6); }
.prerun-intel-pop {
  display:none; position:absolute; left:0; right:0; bottom:calc(100% + ${
    UI_SPACE.xs
  }px); text-align:left;
  background:linear-gradient(180deg,${GT},${GB}); border:1px solid rgba(232, 154, 184,.3);
  border-radius:8px; padding:${UI_SPACE.sm}px ${
    UI_SPACE.md
  }px; box-shadow:0 8px 24px -8px rgba(0,0,0,.8);
}
.prerun-enemy-intel.open .prerun-intel-pop { display:block; animation:prerun-bootin-fade 200ms ease both; }

/* CONSOLE (Task-001, AC-1d) — exactly three element groups: identity (the
   Operator-ID chip + the permanently-visible privacy chip), the three loadout
   slots (AC-3's only entry into the module catalog), and DEPLOY. DEPLOY's
   exact geometric dominance (AC-5) and the row's 40dp tap-target reserve
   (AC-27) are Task-007's contract; this pass wires the frame + the slots. */
/* Task-010 (2026-prestart-deployment-redesign, measured, not speculated) —
   .prerun-panel's shared overflow:hidden (it clips each panel's own
   .prerun-scan/.prerun-etch decoration to its rounded corners) also clips
   ANY descendant that tries to escape the panel visually via
   position:absolute — position:absolute escapes normal-flow LAYOUT, it does
   NOT escape an ancestor's overflow clip. Both of this panel's popovers (the
   module catalog, AC-3/AC-4; the ID setter, AC-22) are built exactly that
   way (bottom:calc(100% + Npx), popping up ABOVE the row), so on this one
   panel the clip silently ate them: getBoundingClientRect() reported correct
   geometry (layout doesn't know about paint clipping) while the catalog was
   actually invisible and un-clickable at 852x393 — confirmed via a real
   screenshot showing the threat rail where the open catalog should have
   been, and document.elementsFromPoint() at a tile's own reported centre
   finding nothing more specific than .prerun-main's flat background. */
/* The console is one instrument face, not a dark strip with controls on it:
   a chamfer at the outer corners, a lit top edge where the light falls on it,
   an inset shadow so it sits BELOW the stage rather than beside it, and a very
   fine technical grid across its ground so the empty middle reads as SURFACE
   rather than as a hole. Nothing here carries information. */
/* DEVICE FIX 2026-08-18 ("Modul-Slots wirken tot"): the chamfer clip-path
   used to sit on the CONTAINER — and clip-path clips EVERY descendant, even
   absolutely-positioned ones, so the then-drop-up module catalog opened
   invisibly and a slot tap read as dead — on web and iPhone alike. The
   chamfer now lives on the two DECOR pseudo layers only (gradient ground +
   grid pattern), which is all it ever visibly clipped; children escape
   freely. (Same device round, later finding: the catalog itself moved into
   the centered modal shell — the clip lesson stays because the pseudo-layer
   chamfer is still what keeps the console's own children free.) The old
   outer drop shadow (0 -10px 26px …) had always been clipped away by the
   very same clip-path, so dropping it changes nothing visible. */
.prerun-console {
  overflow:visible; position:relative;
  /* Above the hero frame (z-index:1): the console's layout never overlaps the
     hero, but the module catalog OPENS upward into that zone — without this
     the heroframe sat on top and swallowed the catalog's pointer events. */
  z-index:2;
  --con-chamfer:polygon(14px 0,calc(100% - 14px) 0,100% 14px,100% 100%,0 100%,0 14px);
}
.prerun-console::before {
  content:""; position:absolute; inset:0; z-index:0; pointer-events:none;
  clip-path:var(--con-chamfer);
  background:linear-gradient(180deg,rgba(14,26,32,.72),rgba(6,10,16,.86));
  box-shadow:
    inset 0 1px 0 color-mix(in srgb,${G} 26%,transparent),
    inset 0 0 40px -18px color-mix(in srgb,${G} 40%,transparent);
}
.prerun-console::after {
  content:""; position:absolute; inset:0; z-index:0; pointer-events:none; opacity:.5;
  clip-path:var(--con-chamfer);
  background:
    repeating-linear-gradient(90deg,rgba(136,224,208,.045) 0 1px,transparent 1px 34px),
    repeating-linear-gradient(0deg,rgba(136,224,208,.03) 0 1px,transparent 1px 26px);
  -webkit-mask-image:linear-gradient(90deg,transparent,#000 18%,#000 70%,transparent);
  mask-image:linear-gradient(90deg,transparent,#000 18%,#000 70%,transparent);
}
.prerun-console > * { position:relative; z-index:1; }
/* The hairline that runs from the identity group to the primary action. It is
   the console's own trace — surface, not a readout — and it is what stops the
   middle from reading as a dead black gap. */
.prerun-advance-hint::before {
  content:""; position:absolute; left:0; right:0; top:50%; height:1px; z-index:-1;
  background:linear-gradient(90deg,transparent,color-mix(in srgb,${G} 42%,transparent) 14%,
             color-mix(in srgb,${G} 42%,transparent) 86%,transparent);
}
/* Two small nodes where the trace meets the groups it connects — the technical
   markings the brief asks for, carrying no reading. */
.prerun-advance-hint::after {
  content:""; position:absolute; left:12%; right:12%; top:50%; height:5px;
  margin-top:-2px; z-index:-1; pointer-events:none;
  background:radial-gradient(closest-side,${G},transparent) left center/5px 5px no-repeat,
             radial-gradient(closest-side,${G},transparent) right center/5px 5px no-repeat;
  filter:drop-shadow(0 0 5px ${G});
}
.prerun-advance-hint { position:relative; }
.prerun-console-body { min-width:0; display:flex; flex-direction:column; gap:${
    UI_SPACE.xxs
  }px; padding:${UI_SPACE.xxs}px ${UI_SPACE.lg}px ${UI_SPACE.xs}px; }
.prerun-console-row { display:flex; align-items:center; gap:${
    UI_SPACE.md
  }px; min-width:0; }
/* position:relative — the AC-22 setter's positioning context (Task-005). */
.prerun-console-identity { position:relative; flex:0 0 auto; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:${
    UI_SPACE.sm
  }px; }
/* AC-27 (2026-prestart-deployment-redesign) — the console-row reserve. This
   group's un-capped flex-grow is exactly why the row measured -0.00 dp of
   reserve (docs/session_log/prerun-rails-2026-08-15.md): with a fixed-width
   chip flush at the row's start and a fixed-width DEPLOY flush at its end,
   this middle group always swallows the ENTIRE gap between them — including
   the space its own three max-width:170px slots never use — so the "reserve"
   AC-27 measures (row inner width minus the outer span from the chip's left
   edge to DEPLOY's right edge) was always exactly zero, REGARDLESS of how
   wide/narrow the slots themselves are: the tiles' own internal slack
   doesn't count, only genuine space at the row's own two ends does. Capping
   this group's growth is therefore the one lever that can create real
   reserve without touching AC-5's DEPLOY-dominance literals or AC-6's chip
   width. The trade: DEPLOY no longer sits perfectly flush against the
   panel's trailing edge — it gains a small, constant margin instead, at
   EVERY profile (the calc(100% - N) form scales with the row's own width,
   unlike a flat dp cap, which measured 60dp at 667x375 but a disproportionate
   410dp at 852x393 in a first pass — see the task's own report for the
   number check). 555 = chip(130) + DEPLOY(340) + 2 row gaps(2x12) + a 61dp
   target reserve — the reference mockup's own measured figure for this
   exact row (spec AC-27's "Referenz misst 61 dp"). */
/* The 'max-width: calc(100% - 555px)' this rule used to carry was written for
   the old console, where the slots shared a row with a 555px ID+DEPLOY pair.
   Inside the merged deployment console the parent is narrow, so that
   expression went negative and the three slots collapsed on top of each other
   (seen at 852x393, 2026-08-15). The group sizes to its own content now. */
.prerun-console-slots { flex:0 0 auto; min-width:0; position:relative; display:flex; flex-direction:column; gap:${
    UI_SPACE.xs
  }px; justify-content:center; }
/* AC-27 — the explicit width is load-bearing, not decorative: measured
   directly (a real Chromium, not read from spec text) — with the slots cap
   above alone, THIS group's flex-basis:auto/width:auto resolved wider than
   its own 340px content once its sibling stopped consuming 100% of the row
   (402 design px measured, ~31dp of dead space centred on each side of the
   340px DEPLOY button via this rule's own align-items:center) — the freed
   space leaked into an auto-sized sibling despite flex-grow:0, cutting the
   real reserve roughly in half (31dp measured, not the intended 61). An
   explicit width pins this group to its content's true size, same number
   Task-007 already declared on .prerun-deploy-btn (AC-5) below. */
.prerun-console-deploy { flex:0 0 auto; min-width:0; display:flex; flex-direction:column; align-items:stretch; justify-content:center; gap:${
    UI_SPACE.xxs
  }px; }
/* the three loadout slots — an EMPTY slot is the only entry into the module
   catalog below (AC-3); a FULL slot shows its pick and clears it on tap. */
.prerun-slotrow { display:flex; gap:${UI_SPACE.sm}px; }
.prerun-slot {
  position:relative; flex:0 0 auto; width:152px; min-height:64px; box-sizing:border-box;
  padding:0 ${UI_SPACE.xs}px; overflow:hidden;
  border:1px solid ${PB}; border-radius:2px; cursor:pointer;
  clip-path:polygon(9px 0,100% 0,100% calc(100% - 9px),calc(100% - 9px) 100%,0 100%,0 9px);
  box-shadow:inset 0 1px 0 rgba(136,224,208,.10), inset 0 0 22px -14px rgba(136,224,208,.7);
  display:flex; flex-direction:column; align-items:center; justify-content:center; gap:${
    UI_SPACE.hair
  }px;
  background:linear-gradient(180deg,${GT},${GB}); transition:border-color .15s,box-shadow .15s;
}
.prerun-slot.empty { border-style:dashed; border-color:rgba(136, 224, 208,.3); }
/* "System erwacht", not arcade neon: a short, local light on the element you
   touched. Nothing travels across the screen, nothing flashes. */
.prerun-slot { transition:border-color 180ms ease, box-shadow 220ms ease, transform 140ms ease; }
.prerun-slot.empty:hover { border-color:${G}; }
.prerun-slot:hover { box-shadow:inset 0 1px 0 rgba(136,224,208,.22), inset 0 0 26px -10px rgba(136,224,208,.9); }
.prerun-slot:active { transform:translateY(1px); }
.prerun-id-chip { transition:border-color 180ms ease, box-shadow 220ms ease, transform 140ms ease; }
.prerun-id-chip:hover { box-shadow:inset 0 1px 0 rgba(136,224,208,.26), inset 0 0 30px -12px rgba(136,224,208,1); }
.prerun-id-chip:active { transform:translateY(1px); }
.prerun-slot.full { border-style:solid; border-color:${G}; box-shadow:0 0 14px -6px rgba(136, 224, 208,.5) inset; }
.prerun-slot-plus { font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "title"
  )}; color:${G}; line-height:1; }
.prerun-slot-label { font-size:${roleFontSize(
    "body"
  )}; letter-spacing:.06em; color:${DIM}; text-align:center; max-width:100%; white-space:nowrap; }
.prerun-slot-ic { width:26px; height:26px; object-fit:contain; filter:drop-shadow(0 0 5px rgba(136, 224, 208,.5)); }
/* Visual-polish fix (2026-08-15) — AC-25's bump to the "body" role (16px
   base, up from whatever this shipped at before) made this a single-line
   ellipsis-truncator ("OVERCLOCKED BO…", "PATTERN RECOGN…" measured on a
   filled loadout slot, real Chromium) for any module name over ~9-10
   characters — unreadable on a screen this premium. Same wrap-not-clip
   idiom .modc-name already uses below (readabilityLayoutNet's WRAP GUARD
   invariant): the slot grows a second line via .prerun-slot's own
   min-height (not a fixed height, so nothing clips) instead of losing the
   tail of a word. Stays well clear of the 11-real-px floor either way. */
.prerun-slot-name {
  max-width:100%; font-size:${roleFontSize(
    "body"
  )}; color:${STEEL}; text-align:center; line-height:1.2;
  overflow-wrap:break-word; word-break:normal;
}
/* the module catalog — hidden until an empty slot opens it (AC-3); it is
   never conditionally built, only shown/hidden, so its tiles stay reachable
   regardless of the toggle. Task-006 (AC-1e/AC-4) — the ONE disclosure
   surface: position:absolute removes it from normal flow entirely (it can
   never push hero/threat-rail content around), anchored from the BOTTOM
   (never top) so it grows UP from the console row, over the threat rail —
   and max-height + internal scroll cap how far it can reach, so it can
   never extend into the hero zone above. */
/* DEVICE ROUND 2026-08-18 (owner) — the three PreRun disclosure surfaces
   (modules · initials · settings) live on the SAME centered modal shell as
   the in-game Einstellungen/Pause (modalShell.ts). These are CONTENT FILLS
   only: width/height budget per surface — the shell owns wash, panel
   material, Escape and the backdrop. Sized against the 852x393 landscape
   stage: height capped so the footer CTA always stays on screen. */
.prerun-mod-modal {
  /* Stage-relative: the DESIGN space is ~1950px wide at the phone profile, so
     any small px cap collapses the five-card scroller into 1-char columns
     (seen 2026-08-18). 92% keeps the owner's "~5 premium cards visible" tile
     math from the old full-width row. */
  width:92%; max-width:96%; max-height:92%; box-sizing:border-box;
  display:flex; flex-direction:column; min-height:0;
}
.prerun-mod-modal .prerun-modules { flex:1 1 auto; min-height:0; align-items:stretch; }
.prerun-mod-modal .prerun-mod-count { flex:0 0 auto; }
.prerun-settings-modal, .prerun-id-modal {
  /* The in-game settings panel's own width (SettingsOverlay: withUiScale(440))
     — one settings surface, one size, everywhere. */
  width:${withUiScale(440)}; max-width:86%; max-height:94%; box-sizing:border-box;
  overflow-y:auto;
}
/* The reused control buttons stretch to the shell row's full width so every
   label — FORTSCHRITT ZURÜCKSETZEN included — renders complete. */
.prerun-settings-modal .modal-shell-row > button { width:100%; }
.prerun-id-modal .prerun-idbox { width:100%; }
/* Larger cells: the drop-up's 44 DESIGN-px cells landed at ~19 REAL px after
   the stage transform — the "funktioniert nicht richtig" on device. In the
   roomy modal the tap-to-cycle targets take COUNTER-SCALED floor-based
   sizes (the same trap-avoidance as the ⚙ above), and the glyph grows with
   the cell. Beats the scale-l 48px override by specificity+order. */
#pre-run-screen.prerun-scale-l .prerun-id-modal .prerun-namecell,
.prerun-id-modal .prerun-namecell {
  width:calc(${PRERUN_TOUCH_FLOOR} * 1.0);
  height:calc(${PRERUN_TOUCH_FLOOR} * 1.18);
  line-height:calc(${PRERUN_TOUCH_FLOOR} * 1.18);
  font-size:calc(${PRERUN_TOUCH_FLOOR} * 0.58);
}
.prerun-id-modal .prerun-namefield { gap:12px; justify-content:center; }
.prerun-id-modal .prerun-callsign { align-items:center; }
.prerun-id-modal .prerun-idbox { padding:${UI_SPACE.md}px ${UI_SPACE.xl}px; }
/* One centred CTA per PreRun modal — the shell footer is a bare flex row. */
.prerun-mod-modal .modal-shell-foot,
.prerun-settings-modal .modal-shell-foot,
.prerun-id-modal .modal-shell-foot { justify-content:center; }
/* shared chamfered frame: a 2px ring of border colour (outer bg) around the inner
   ::before fill, both clipped to the same bevel — clean cut corners + a glow that
   follows the bevel (drop-shadow, not box-shadow which clip-path would crop). */
.prerun-idbox, .prerun-deploy-btn {
  --chamfer:polygon(12px 0,calc(100% - 12px) 0,100% 12px,100% calc(100% - 12px),calc(100% - 12px) 100%,12px 100%,0 calc(100% - 12px),0 12px);
  position:relative; clip-path:var(--chamfer); min-height:90px; padding:0; border:0; box-sizing:border-box;
  max-width:100%;
}
.prerun-idbox::before, .prerun-deploy-btn::before { content:""; position:absolute; inset:2px; clip-path:var(--chamfer); z-index:0; }
.prerun-idbox > *, .prerun-deploy-inner { position:relative; z-index:1; }
/* LEFT — OPERATOR ID */
.prerun-idbox { background:color-mix(in srgb, ${G} 32%, transparent); display:flex; flex-direction:column; align-items:center; justify-content:center; gap:${
    UI_SPACE.sm
  }px; }
.prerun-idbox::before { background:linear-gradient(180deg,${GT},${GB}); }
.prerun-idbox .prerun-callsign { flex-direction:row; align-items:center; justify-content:center; gap:${
    UI_SPACE.md
  }px; }
.prerun-id-ic { width:24px; height:24px; object-fit:contain; filter:drop-shadow(0 0 5px rgba(136, 224, 208,.5)); }
/* Task-005 (2026-prestart-deployment-redesign, AC-6) — the compact
   Operator-ID CHIP. Its declared width is the AC-5 baseline DEPLOY must
   double (see .prerun-deploy-btn below). */
/* The reference's ID control: crest, the value, an edit affordance on one
   row, with the "tap to change" hint beneath. The VALUE is still exactly the
   three characters D-AC-13 locks (the reference draws a longer callsign; that
   is illustration, and initials.ts is LOCKED under D-126) — only the frame
   around it changed. */
.prerun-id-chip {
  width:auto; min-width:190px; min-height:64px; box-sizing:border-box; flex:0 0 auto;
  padding:0 ${UI_SPACE.md}px;
  clip-path:polygon(9px 0,100% 0,100% calc(100% - 9px),calc(100% - 9px) 100%,0 100%,0 9px);
  box-shadow:inset 0 1px 0 rgba(136,224,208,.12), inset 0 0 26px -16px rgba(136,224,208,.8);
  display:grid; grid-template-columns:auto 1fr auto; align-items:center;
  justify-items:start; column-gap:${UI_SPACE.sm}px; row-gap:0;
  border:1px solid rgba(136, 224, 208,.3); border-radius:8px; cursor:pointer;
  background:linear-gradient(180deg,${GT},${GB});
  box-shadow:0 0 14px -6px rgba(136, 224, 208,.4) inset;
  font-family:${UI_FONT_STACK}; transition:border-color .15s;
}
.prerun-id-chip:hover { border-color:${G}; }
.prerun-id-chip.empty { border-color:rgba(232,208,152,.5); }
.prerun-id-chip-lab { grid-column:1 / -1; font-size:${roleFontSize(
    "body"
  )}; letter-spacing:.16em; color:${DIM}; text-align:left; }
.prerun-id-chip-val { font-size:${roleFontSize(
    "title"
  )}; letter-spacing:.14em; color:${GH}; text-shadow:0 0 10px rgba(136, 224, 208,.4); }
.prerun-id-chip.empty .prerun-id-chip-val { color:${COLORS.AMBER_WARN}; }

/* Task-005 (AC-22/AC-24) — the setter's privacy notice: permanently visible
   the instant the setter modal opens (no hover/click needed) and BEFORE the
   input (DOM order). The link is a real, directly-tappable button — not a
   second hover reveal. (The setter SURFACE itself is the centered
   .prerun-id-modal shell fill above — device round 2026-08-18.) */
.prerun-id-privacy { display:flex; flex-direction:column; gap:${
    UI_SPACE.xs
  }px; text-align:left; }
.prerun-id-privacy-notice { margin:0; font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "body"
  )}; line-height:1.4; color:${DIM}; }
/* RIGHT — DEPLOY button: ACTIVE (glowing green) */
/* CONTINUE is the endpoint: it is the only element on this band that carries
   its own light into the room around it. */
.prerun-deploy-btn {
  cursor:pointer; background:${GH};
  filter:drop-shadow(0 0 16px rgba(136, 224, 208,.6)) drop-shadow(0 0 42px rgba(136, 224, 208,.28));
  transition:filter .2s, transform 140ms ease;
}
/* The armed state carries a slow charge. 0.24 Hz — a quarter of the charter's
   ceiling — and the amplitude is small enough that it reads as a system under
   power rather than as a blinking control. A locked button never breathes. */
.prerun-deploy-btn:not(.locked) { animation:prerun-armed 4.2s ease-in-out infinite; }
@keyframes prerun-armed {
  0%,100% { filter:drop-shadow(0 0 14px rgba(136,224,208,.52)) drop-shadow(0 0 34px rgba(136,224,208,.20)); }
  50%     { filter:drop-shadow(0 0 20px rgba(136,224,208,.72)) drop-shadow(0 0 52px rgba(136,224,208,.34)); }
}
.prerun-deploy-btn:not(.locked):active { transform:translateY(1px) scale(.985); }
/* Task-007 (AC-5) — DEPLOY dominates geometrically: >=2x the AC-6 chip's
   130px (260px) and wider than a loadout slot's 170px max-width — declared
   here as the ONE literal both invariants are checked against. */
.prerun-deploy-btn { width:340px; }
.prerun-deploy-btn::before { background:radial-gradient(130% 150% at 50% 0%, color-mix(in srgb, ${G} 42%, ${GB}), ${GB} 80%); }
.prerun-deploy-inner { display:flex; flex-direction:column; align-items:center; justify-content:center; height:100%; gap:2px; }
.prerun-deploy-inner { display:flex; align-items:center; justify-content:center; padding:0 26px; }
.prerun-deploy-main {
  font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "display"
  )}; letter-spacing:.3em;
  color:${GH}; text-shadow:0 0 16px rgba(136, 224, 208,.8);
  /* One line, always. CONTINUE wrapped to two at 852x393 and grew the console
     band, which is the same D-AC-3 class the caption and the hint hit. */
  white-space:nowrap;
}
/* Act 1's primary action is WEITER/CONTINUE — a longer word than DEPLOY at the
   same display role, so it gets the tighter tracking rather than a wrap. */
.prerun-advance-btn .prerun-deploy-main { letter-spacing:.10em; font-size:${roleFontSize(
    "title"
  )}; }
.prerun-deploy-btn:hover { filter:drop-shadow(0 0 22px rgba(136, 224, 208,.85)); }
/* RIGHT — DEPLOY button: INACTIVE (just darker, same shape + look) */
.prerun-deploy-btn.locked { cursor:not-allowed; background:rgba(136, 224, 208,.16); filter:none; }
.prerun-deploy-btn.locked::before { background:linear-gradient(180deg,${GT},${GB}); }
.prerun-deploy-btn.locked .prerun-deploy-main { color:${GD}; text-shadow:none; }

/* ══════════════════════════════════════════════════════════════════════════
   SCREEN 2 · FACE THE THREAT — the same language, a different temperature.

   Everything below is scoped to .prerun-act2. Screen 1 is approved and frozen;
   scoping is what makes that a fact rather than a promise.

   The shared stage (depth, key light, grain, corner ticks, rotary recession,
   rim, console material, typography) is inherited unchanged — that is the
   point of building both acts on one component. What act 2 changes is
   TEMPERATURE, and it changes it with the one thing the tiers already own:
   their colour. --tier-ac is the focused tier's ink and --esc is its position
   in the never-reordered ladder, 0..1, published by renderThreat.

   Both drive LIGHT ONLY. No digit, no word, no comparison — D-AC-10 and AC-21
   forbid claiming a mechanical difference between tiers, and atmospheric
   fiction is what they expressly allow. The escalation you feel here is the
   room getting darker and the edge getting hotter, not a stat.
   ══════════════════════════════════════════════════════════════════════════ */

/* Composition. Screen 1 places the operator off-centre — you, considered.
   FENRIR is dead centre, because a thing that faces you does not stand to one
   side. Same component, opposite posture. */
#pre-run-screen.prerun-act2 .prerun-herocap { width:30%; max-width:30%; }

/* THE FACE MOVES TO THE OPTICAL CENTRE. object-fit:contain fits the whole
   512x768 plate into the position's box, so where the eyes land is decided by
   the artwork, not by us: measured against the source, they sat at 172 dp in a
   frame whose middle is 160, with dead air above the portal and a slab of black
   coat filling the bottom. A 4 % lift moves the eyes just above the middle and
   pushes that much more of the coat past the crop — where the scrim was already
   dissolving it. Static, so every selection state shifts alike and the FRAME's
   own rect (what D-AC-3 measures) does not move at all. Act 2 only: screen 1's
   figure is framed for a different composition and is frozen. */
#pre-run-screen.prerun-act2 .prerun-rot { top:-4%; }

/* ── The narrow profile's one real collision (D-AC-4) ─────────────────────────
   FENRIR stands dead centre, so his column begins at 33 % of the frame. The act
   title does not shrink with the frame — it is chrome, sized off the type scale
   — so as the viewport narrows the column walks left into it while the title
   stays put. Swept in the browser rather than guessed (gap = column left minus
   title right, act 2, one measurement per width):

     667  -26.5   700  -22.2   720  -15.6   740   -9.0   760   -2.5   <- collide
     780   +4.1   800  +10.8   820  +17.4   844  +25.3   852  +27.9   <- clear

   So the onset is between 760 and 780, and 780 is the breakpoint. Everything at
   or above it — including both 852 profiles and every screen 1 case — is not
   matched at all and stays byte-identical; this is why the fix lives in a media
   query and not in the act-2 block above.

   The title is the only thing allowed to give: the hero's composition and the
   rotary's geometry are frozen, and moving the title left would undo the
   BACK/title separation from the 2026-08-15 header review. It gives 12 % of its
   size and half its tracking, which at 667 takes it from 134.7 to ~99 real px
   and leaves ~9 px of air before the column. Act 2 only — act 1 puts its figure
   at --reel-cx 67 %, so its column starts at 334 real px at 667 and was never
   in reach. */
@media (max-width:780px){
  #pre-run-screen.prerun-act2 .prerun-act-title {
    font-size:calc(${roleFontSize("heading")} * .88);
    letter-spacing:.12em;
  }
}
/* The lift moves the crop window with it: the frame now shows the host box from
   4/142 to 104/142 instead of 0 to 100/142. Both ends divided by the scale for
   the same reason as the base rule. */
#pre-run-screen.prerun-act2 .prerun-rot-prot {
  top:calc(2.82% / var(--rot-s,1));
  bottom:max(0%, calc(100% - 73.24% / var(--rot-s,1)));
}

/* The room darkens as the ladder rises. At sandbox this is barely a change;
   at fallout the edges have closed in.

   Three jobs beyond temperature, all of them light rather than UI:

   1 A LIGHT CONE from above. Screen 1 is open, lateral, mint — a room you are
     standing in. This is a shaft down onto one thing, which is what makes the
     screen read as confrontation instead of selection.
   2 A HARD VIGNETTE. Screen 1 breathes at its edges; here they close.
   3 THE LEFT THIRD GOES BACK TO NEGATIVE SPACE. The pale rectangle beside
     CONTAINMENT was never a panel — it is a lit corridor wall inside the
     blurred backdrop, whose own edge happens to fall vertically. Nothing to
     delete, so it gets unlit: the caption then sits in darkness with its
     hairline, exactly as SENTINEL-7 does, and the block stops reading as a
     plate because there is no longer a plate to read. */
#pre-run-screen.prerun-act2 .prerun-depth {
  background:
    radial-gradient(30% 62% at 50% -6%,
      color-mix(in srgb,var(--tier-ac,${CRIM}) calc(12% + 8% * var(--esc,0)),rgba(228,240,255,.10)),transparent 72%),
    radial-gradient(56% 72% at 50% 46%,
      color-mix(in srgb,var(--tier-ac,${CRIM}) calc(9% + 7% * var(--esc,0)),transparent),transparent 68%),
    linear-gradient(90deg,rgba(3,6,10,.90) 0%,rgba(3,6,10,.66) 18%,rgba(3,6,10,.22) 32%,transparent 46%),
    linear-gradient(270deg,rgba(3,6,10,.72) 0%,rgba(3,6,10,.24) 16%,transparent 34%),
    radial-gradient(112% 108% at 50% 36%,transparent calc(30% - 12% * var(--esc,0)),rgba(3,6,10,.86) 100%),
    linear-gradient(180deg,rgba(3,6,10,.60) 0%,transparent 24%,transparent 56%,rgba(3,6,10,.86) 100%);
}
/* The rims take his colour, and they close in with the ladder. */
#pre-run-screen.prerun-act2 .prerun-rim.l {
  background:linear-gradient(90deg,
    color-mix(in srgb,var(--tier-ac,${CRIM}) calc(12% + 12% * var(--esc,0)),transparent),transparent 70%);
}
#pre-run-screen.prerun-act2 .prerun-rim.r {
  background:linear-gradient(270deg,
    color-mix(in srgb,var(--tier-ac,${CRIM}) calc(12% + 12% * var(--esc,0)),transparent),transparent 70%);
}
/* The key light behind him is his own, not the operator's mint. */
#pre-run-screen.prerun-act2 .prerun-keylight {
  background:
    radial-gradient(26% 22% at 50% 20%,
      color-mix(in srgb,var(--tier-ac,${CRIM}) 40%,rgba(240,248,255,.30)),transparent 74%),
    radial-gradient(42% 36% at 50% 30%,
      color-mix(in srgb,var(--tier-ac,${CRIM}) 26%,transparent),transparent 76%),
    radial-gradient(76% 68% at 50% 56%,rgba(4,8,14,.72),transparent 76%);
}
/* THE ESCALATION LIVES ON THE EDGE. Screen 1's rim is mint because she is
   yours; his is the tier's own ink, and it burns harder the further up the
   ladder the focused tier sits. This is the cyan -> red dramaturgy the spec
   names, carried by colour rather than by copy. */
#pre-run-screen.prerun-act2 .prerun-rot.on .prerun-rot-img {
  filter:
    brightness(calc(1.05 + 0.05 * var(--esc,0))) contrast(calc(1.06 + 0.08 * var(--esc,0))) saturate(1.05)
    drop-shadow(-1px 0 0 color-mix(in srgb,var(--rot-ac,${CRIM}) calc(22% + 20% * var(--esc,0)),transparent))
    drop-shadow(1px 0 0 color-mix(in srgb,var(--rot-ac,${CRIM}) calc(16% + 20% * var(--esc,0)),transparent))
    drop-shadow(0 0 calc(6px + 10px * var(--esc,0)) color-mix(in srgb,var(--rot-ac,${CRIM}) 20%,transparent))
    drop-shadow(0 0 46px rgba(0,0,0,.96));
}
/* WHY THE RIM CAME DOWN. drop-shadow follows the alpha channel, and on this
   asset the alpha silhouette is the PORTAL — the amber cage is painted into the
   art (512x768, transparent ground), so every rim we lit was lighting the cage
   and nothing else. The frame ended up brighter than the face it frames. The
   asset is frozen and the cage cannot be removed, so it gets UNLIT instead:
   less rim, and a radial that keeps light on the head while the periphery,
   which is where the cage lives, falls into the dark. The cage now shapes him.
   The eyes are the only thing in the picture allowed to be the brightest. */
#pre-run-screen.prerun-act2 .prerun-rot.on::after {
  content:""; position:absolute; inset:-4% -12% 0; z-index:3; pointer-events:none;
  background:radial-gradient(38% 26% at 50% 33%,
    transparent,transparent 52%,rgba(3,6,10,calc(.34 + .22 * var(--esc,0))) 100%);
}
/* The standing light gets a second, much tighter lobe BEHIND the head (the
   base rule puts this pseudo at z-index -1, so it separates him from the room
   rather than washing over him). Screen 1's key light is ambient; this one is
   aimed. It does not move, so the charter's 1 Hz ceiling is not in play. */
#pre-run-screen.prerun-act2 .prerun-rot.on::before {
  background:
    radial-gradient(22% 15% at 50% 30%,
      color-mix(in srgb,var(--rot-ac,${CRIM}) 30%,rgba(240,248,255,.22)),transparent 78%),
    radial-gradient(closest-side,
      color-mix(in srgb,var(--rot-ac,${CRIM}) calc(18% + 8% * var(--esc,0)),transparent),transparent 74%);
}
/* Corner ticks and the identity hairline join the temperature. */
#pre-run-screen.prerun-act2 .prerun-ticks i { border-color:var(--tier-ac,${CRIM}); }
#pre-run-screen.prerun-act2 .prerun-herocap::before {
  background:linear-gradient(180deg,transparent,
    color-mix(in srgb,var(--tier-ac,${CRIM}) 66%,transparent) 22%,
    color-mix(in srgb,var(--tier-ac,${CRIM}) 66%,transparent) 74%,transparent);
}
#pre-run-screen.prerun-act2 .prerun-herocap::after,
#pre-run-screen.prerun-act2 .prerun-caprule {
  background:var(--tier-ac,${CRIM}); box-shadow:0 0 6px var(--tier-ac,${CRIM});
}
#pre-run-screen.prerun-act2 .prerun-caprule::after {
  background:linear-gradient(90deg,
    color-mix(in srgb,var(--tier-ac,${CRIM}) 46%,transparent),transparent 88%);
  box-shadow:none;
}
#pre-run-screen.prerun-act2 .prerun-heroname { color:var(--tier-ac,${CRIM}); }
#pre-run-screen.prerun-act2 .prerun-heroname.locked { color:${STEEL}; text-shadow:none; }
#pre-run-screen.prerun-act2 .prerun-heroline { color:${STEEL}; }
/* The reel's own marks follow him too — but they also need a GROUND here.
   The row is centred in the frame; on act 1 the figure stands off at 67 %, so
   the marks fell into empty stage and read on their own. Centred, they land on
   the darkest mass in the whole picture (his jacket) and vanish at 26 %. Same
   class as the runner that once started at the frame's left edge: a position
   that was only ever right under act 1's staging. Fixed with the shared
   vocabulary rather than new geometry — a scrim beneath the row, and marks lit
   for the ground they now sit on. */
#pre-run-screen.prerun-act2 .prerun-pips::before {
  content:""; position:absolute; left:50%; top:0; bottom:0; width:360px;
  margin-left:-180px; z-index:0; pointer-events:none;
  background:radial-gradient(58% 96% at 50% 62%,rgba(2,6,10,.82),transparent 74%);
}
#pre-run-screen.prerun-act2 .prerun-pip i {
  background:color-mix(in srgb,var(--tier-ac,${CRIM}) 44%,transparent);
  box-shadow:0 0 0 1px rgba(2,6,10,.7);
}
#pre-run-screen.prerun-act2 .prerun-pip.on i { background:var(--tier-ac,${CRIM}); }
#pre-run-screen.prerun-act2 .prerun-piprunner::before {
  background:var(--tier-ac,${CRIM}); box-shadow:0 0 12px var(--tier-ac,${CRIM});
}
#pre-run-screen.prerun-act2 .prerun-rot-arw i { border-color:var(--tier-ac,${CRIM}); }

/* ── Zone 3 · the threat context ─────────────────────────────────────────────
   It stays CONTENTLESS — D-AC-10 forbids a mechanical claim between the tiers,
   and the spec's open question 2 (per-tier fiction: does it exist, who writes
   it) is unanswered; its own instruction is that without an answer the surface
   stays empty. The first build gave that emptiness MATERIAL — a fill, a lit top
   edge, a chamfer, a hairline — and an empty surface with material is exactly
   how a UI panel that failed to load looks. So the material goes and the zone
   stays: D-AC-2 requires four zones on screen 2, and this is one of them.

   What it becomes is a strip of the STAGE. margin-top cancels its own height,
   so the flex row hands the space to the hero zone (flex:1 1 auto) and the
   picture runs all the way down to the console; the strip then paints back over
   the last band of it, carrying nothing but the one control that was always
   here. No measurement and no JS — the height is the touch floor either way. */
#pre-run-screen.prerun-act2 .prerun-threatctx {
  position:relative; z-index:6;
  height:${PRERUN_TOUCH_FLOOR}; margin-top:calc(-1 * (${PRERUN_TOUCH_FLOOR}));
  background:none; box-shadow:none; clip-path:none; pointer-events:none;
}
#pre-run-screen.prerun-act2 .prerun-threatctx-body {
  height:100%; justify-content:flex-end; padding-top:0; padding-bottom:0;
}
#pre-run-screen.prerun-act2 .prerun-threatctx .prerun-console-spacer { display:none; }
#pre-run-screen.prerun-act2 .prerun-threatctx > * { pointer-events:auto; }
/* FENRIR dissolves downward instead of ending on a cut, the way the operator
   does on screen 1. The scrim lives in the frame, under the strip, so the last
   thing above the console is darkness rather than an edge. It reaches a little
   higher than the fade strictly needs, because the slab of black coat under the
   chest was competing with the head for weight — dissolving it earlier is the
   other half of moving the eyes to the optical centre. */
#pre-run-screen.prerun-act2 .prerun-heroframe::after {
  content:""; position:absolute; left:0; right:0; bottom:0; height:40%;
  z-index:3; pointer-events:none;
  background:linear-gradient(180deg,transparent,rgba(3,6,10,.58) 42%,rgba(2,5,9,.94) 100%);
}
/* ENEMY INTEL is a SECONDARY HUD CONTROL, not a box. Sitting on the seam
   between stage and console with a filled ground, a full border and an 8px
   radius, it read as a card glued to the picture. So the box dissolves and the
   HUD language takes over — the same two corner brackets the stage's own ticks
   use, and nothing else until you touch it. It stays exactly where it was and
   keeps the whole touch floor (D-AC-18); what changes is only how much surface
   it claims. DEPLOY is unaffected and stays 2.7x its width (D-AC-12). */
#pre-run-screen.prerun-act2 .prerun-intel-btn {
  position:relative;
  color:color-mix(in srgb,var(--tier-ac,${CRIM}) 74%,${STEEL});
  border:none; border-radius:0; background:none;
  padding:${UI_SPACE.sm}px ${UI_SPACE.md}px;
  text-shadow:0 1px 6px rgba(2,5,9,.9);
}
/* Two brackets instead of four edges — it reads as HUD, and it reads as less. */
#pre-run-screen.prerun-act2 .prerun-intel-btn::before,
#pre-run-screen.prerun-act2 .prerun-intel-btn::after {
  content:""; position:absolute; width:7px; height:7px; pointer-events:none;
  border-color:color-mix(in srgb,var(--tier-ac,${CRIM}) 46%,transparent);
  transition:border-color 180ms ease;
}
#pre-run-screen.prerun-act2 .prerun-intel-btn::before {
  left:0; top:22%; border-left:1px solid; border-top:1px solid;
}
#pre-run-screen.prerun-act2 .prerun-intel-btn::after {
  right:0; bottom:22%; border-right:1px solid; border-bottom:1px solid;
}
#pre-run-screen.prerun-act2 .prerun-intel-btn:hover {
  color:var(--tier-ac,${CRIM}); border-color:transparent; box-shadow:none;
}
#pre-run-screen.prerun-act2 .prerun-intel-btn:hover::before,
#pre-run-screen.prerun-act2 .prerun-intel-btn:hover::after {
  border-color:var(--tier-ac,${CRIM});
}

/* ── Zone 4 · the readout ────────────────────────────────────────────────────
   One row: three cells, each carrying its own state pip, its label and its
   committed value, joined by the traces that only conduct as far as the first
   unmet condition. */
.prerun-readout { display:flex; align-items:center; flex:1 1 auto; min-width:0; }
.prerun-ro-cell {
  display:flex; flex-direction:column; align-items:flex-start; gap:1px;
  flex:0 1 auto; min-width:0; overflow:hidden;
}
.prerun-ro-head { display:flex; align-items:center; gap:${
    UI_SPACE.hair
  }px; min-width:0; }
.prerun-readout .prerun-sum-v { padding-left:calc(8px + ${UI_SPACE.hair}px); }
/* THE ROW IS AN INSTRUMENT BLOCK, NOT A JUSTIFIED LINE. The traces are
   flex:1 1 0, so on the wide profile they stretched the three readings from
   x=13 to x=517 with the values orphaned in the middle of nowhere — measured
   at 852x393. Three facts spread over 500 px read as three unrelated facts.
   Capping the trace turns them back into one connected instrument sitting at
   the left, and hands every pixel it gives up to the primary action. The
   language is unchanged: same pips, same labels, same traces, same spark. */
#pre-run-screen.prerun-act2 .prerun-readout { flex:0 1 auto; }
#pre-run-screen.prerun-act2 .prerun-bus-trace { flex:0 0 auto; width:${
    UI_SPACE.xxl
  }px; }
/* …but compact is not the same as cramped. Against a hero this size the cluster
   was reading small and squeezed, so the air goes INSIDE it rather than back
   between the cells: label and value get a line of separation, the value sits
   at its own weight, and the block keeps a hair more leading. Same three
   readings, same width class, no copy — micro-typography only. */
#pre-run-screen.prerun-act2 .prerun-ro-cell { gap:3px; }
#pre-run-screen.prerun-act2 .prerun-ro-head { gap:${UI_SPACE.xs}px; }
#pre-run-screen.prerun-act2 .prerun-bus-lb { letter-spacing:.2em; }
#pre-run-screen.prerun-act2 .prerun-readout .prerun-sum-v {
  letter-spacing:.1em; line-height:1.25;
}
#pre-run-screen.prerun-act2 .prerun-bus-trace.out { display:none; }
/* DEPLOY takes the free width. D-AC-12 asks the primary action to dominate the
   widest competing interactive element on its own screen — on act 2 that is
   ENEMY INTEL, and this is what makes the margin unambiguous rather than
   arithmetic. */
#pre-run-screen.prerun-act2 .prerun-console-deploy { flex:1 1 auto; margin-left:auto; max-width:34%; }
#pre-run-screen.prerun-act2 .prerun-deploy-btn { width:100%; }
/* DEPLOY is the endpoint of act 2 exactly as CONTINUE is of act 1, and it
   burns in his colour once the circuit reaches it. */
#pre-run-screen.prerun-act2 .prerun-deploy-btn:not(.locked) {
  animation:prerun-armed-hot 4.2s ease-in-out infinite;
}
@keyframes prerun-armed-hot {
  0%,100% { filter:drop-shadow(0 0 14px rgba(136,224,208,.52)) drop-shadow(0 0 34px rgba(136,224,208,.18)); }
  50%     { filter:drop-shadow(0 0 22px rgba(136,224,208,.76)) drop-shadow(0 0 56px rgba(136,224,208,.32)); }
}

/* ── Responsive ─────────────────────────────────────────────────────── */
/* Viewport-width breakpoints removed: this screen renders inside the fixed
   1600×900 stage and is scaled to fit, so the 5-model row keeps its intended
   layout on every device (a viewport-width query would misfire — the element is
   always 1600px wide). The pointer:coarse rule stays: it enlarges touch targets
   on touch devices, which is orthogonal to the stage scale. */
@media (pointer:coarse){
  /* Task-007's own base rule (.prerun-idbox, .prerun-deploy-btn) declares
     min-height:90px — this line used to override it DOWN to a bare 48px at
     equal specificity (source order wins the cascade tie), the exact CAP-
     not-floor bug modalShell.ts's MODAL_TOUCH_FLOOR doc-block names and
     tests/ui/touchTargets.test.ts guards against elsewhere on this repo.
     PRERUN_TOUCH_FLOOR both fixes the regression and clears AC-27's 44 real
     px on DEPLOY's locked state (measured 148.47x29.26 real px at 852x393). */
  .prerun-deploy-btn { min-height:${PRERUN_TOUCH_FLOOR}; }
  .prerun-topbar-back { min-height:40px; }
  /* S188 — the carousel arrows meet the 44px touch floor too. */
  .prerun-cararrow { min-width:44px; min-height:48px; }

  /* AC-27 (2026-prestart-deployment-redesign) — the measured in-scope
     violators (docs/session_log/prerun-rails-2026-08-15.md): the empty
     operator-ID chip and the three loadout slots only failed on height
     (both already clear 44 real px on width, so min-width stays untouched
     to avoid disturbing AC-5/AC-6's own declared widths); the module-
     catalog close, the id-setter close and the privacy link failed on
     BOTH axes for the close pills, height alone for the link (already
     115.82 real px wide). All raised to the shared PRERUN_TOUCH_FLOOR;
     display:inline-flex + centering keeps each button's own label centred
     in its now-taller/-wider tap zone instead of hugging the old corner. */
  /* PRE-EXISTING D-AC-18 VIOLATION, fixed scoped. GearMenu declares its own
     touch floor as a bare "min-height:44px; min-width:44px" (GearMenu.ts:316).
     Inside this stage that is 44 DESIGN px, which the stage transform shrinks
     to ~19 real px — measured 19x22 at 852x393 and 18x22 at 667x375 with
     pointer:coarse emulated, 2026-08-15. It is exactly the trap this file's
     PRERUN_TOUCH_FLOOR doc-block describes ("never a bare design-px literal
     inside the stage"). Overridden here rather than in GearMenu because that
     component is shared with the in-game HUD, which sits in a different scale
     context; changing it there would be an unmeasured change to another
     surface. The HUD's own gear is NOT covered by this and remains open. */
  /* GearMenu injects its own stylesheet, and at equal specificity source order
     decides — which it wins. The id raises specificity so the override holds
     no matter which sheet lands last. */
  #pre-run-screen .prerun-gear-btn {
    min-height:${PRERUN_TOUCH_FLOOR}; min-width:${PRERUN_TOUCH_FLOOR};
  }
  .prerun-id-chip { min-height:${PRERUN_TOUCH_FLOOR}; }
  .prerun-slot { min-height:${PRERUN_TOUCH_FLOOR}; }
  .prerun-privacy-link {
    min-height:${PRERUN_TOUCH_FLOOR};
    display:inline-flex; align-items:center; justify-content:center;
  }
}
/* NOTE: the OS reduced-motion media query is intentionally NOT honored here — the
   pre-run deploy/glitch atmosphere stays ON regardless of the OS setting
   (MotionSettings neutralises that signal app-wide). */
`;
}
